import React, { useState, useEffect, useCallback, useRef } from "react";
import "./ClaimsLedgerPage.css";
import { formatPatientName } from "../utils/formatName";
import { formatCurrency } from "../utils/formatCurrency";
import { formatIcd10List } from "../utils/formatIcd10";
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from "../utils/dateInputBounds";
import useCapabilities from "../hooks/useCapabilities";
import ClaimPostings from "../features/money/ClaimPostings";
import FollowupChip, { fetchFollowupMap } from "../features/money/FollowupChip";
import {
  ClaimEditor,
  openClaimEditor,
  readClaimEditorTarget,
  clearClaimEditorTarget,
} from "../features/claims";
import { SubmitToQueueButton, usePmsGates } from "../features/queue";
import { CopyLastChargeButton } from "../features/charges";
import ExportButton from "../features/reports/ExportButton";
import {
  statusGroups,
  displayStatusKey,
  statusColor,
  claimStatusKey,
  claimStatusText,
} from "../utils/claimStatus";
import {
  clearLedgerLink,
  peekLedgerLink,
} from "../features/reports/ledgerLink";
import RejectionReason from "../components/RejectionReason";
import ClaimListActions from "../features/claimLists/ClaimListActions";
import ClaimListsStrip from "../features/claimLists/ClaimListsStrip";

const API_URL = process.env.REACT_APP_API_URL || "";

// Claim comments were hidden 2026-08-08 while David thought about the feature; turned
// back on 2026-08-09 to work on it live. Nothing was ever deleted — the claim_comments
// table, its migration, all three API routes and every test stayed as they were, so this
// constant is the only thing that ever stood between the feature and the user.
//
// Deliberately NOT a feature flag in the product sense. It is not read from the server,
// not stored per practice, and the routes do not consult it — that shape was offered and
// declined, because one constant in one file is easier to reverse than two moving parts.
//
// Set false and three things go: the Comments tab (History becomes the whole panel), the
// composer, and the row indicator. The API keeps working; this hides the UI, nothing else.
// It is checked in every one of those places rather than at one wrapper, because a wrapper
// is exactly what a later refactor deleted, leaving a kill switch that killed nothing.
const SHOW_CLAIM_COMMENTS = true;

// The panel tab for one claim. With comments off there is only History to show, so the
// stored preference is ignored rather than trusted -- otherwise a claim whose tab was set
// to 'comments' before the flag flipped would render an empty pane.
function panelTabFor(detailTab, claimId) {
  // An explicit selection (e.g. the rules chip jumping to the Rules tab) wins
  // even when comments are feature-flagged off; the flag only changes defaults.
  if (!SHOW_CLAIM_COMMENTS) return detailTab[claimId] || "history";
  return detailTab[claimId] || "comments";
}

// Readable labels for claim-history event sources (avoid raw "stedi_276").
const SOURCE_LABELS = {
  stedi_276: "Payer 276/277",
  ecw: "eCW",
  atlas: "Atlas",
  clearinghouse: "Clearinghouse",
  era: "ERA (835)",
  import: "Import",
};

// A note glyph, drawn inline because this app has no icon dependency — matches the
// sidebar's 24-viewBox, currentColor, stroke-2 convention, just smaller.
function CommentFlagIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

const COMMENT_PREVIEW_LENGTH = 280; // keep in step with routes/ledger.py

// --- Rules engine surfaces -------------------------------------------------
// Chip in the ledger row: the claim's cached presubmit verdict. The submit-time
// engine run is authoritative — this is a display cache (see rules_engine.py).
function RulesChip({ claim, onOpen }) {
  const v = claim.rules_verdict;
  if (!v)
    return (
      <span className="rules-chip rules-chip-none" title="Not yet evaluated">
        —
      </span>
    );
  const detail = claim.rules_verdict_detail || [];
  const label =
    v === "clean"
      ? "Clean ✓"
      : v === "flagged"
        ? `Flagged ⚠${detail.length ? ` (${detail.length})` : ""}`
        : "Blocked ✖";
  const title = detail.length
    ? detail.map((d) => `${d.action_type}: ${d.reason}`).join("\n")
    : v === "clean"
      ? "No rules objected"
      : v;
  return (
    <button
      type="button"
      className={`rules-chip rules-chip-${v}`}
      title={title}
      onClick={onOpen}
    >
      {label}
    </button>
  );
}

// Detail-panel tab: the firing receipts for this claim, fetched lazily.
function RulesPanel({ claimId, verdict, detail, adviseMode }) {
  const [firings, setFirings] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/rules/claims/${claimId}/firings`, {
      credentials: "include",
    })
      .then((r) =>
        r.ok
          ? r.json()
          : Promise.reject(new Error("Failed to load rule receipts")),
      )
      .then((data) => {
        if (!cancelled) setFirings(data.firings || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [claimId]);

  return (
    <div className="rules-panel">
      {adviseMode && (
        <div className="rules-advise-note">
          Rules preview — the engine is in advise mode for this practice:
          verdicts and would-be changes are recorded but nothing is modified or
          gated.
        </div>
      )}
      {verdict && (
        <div className={`rules-panel-verdict rules-chip-${verdict}`}>
          Verdict: {verdict}
          {(detail || []).length > 0 && (
            <ul className="rules-reasons">
              {detail.map((d, i) => (
                <li key={i}>
                  <strong>{d.rule_key}</strong> v{d.version} · {d.action_type}:{" "}
                  {d.reason}
                  {d.proposed != null
                    ? ` (proposed: ${String(d.proposed)})`
                    : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error ? (
        <div className="stream-error">{error}</div>
      ) : firings === null ? (
        <div className="rules-loading">Loading receipts…</div>
      ) : firings.length === 0 ? (
        <div className="rules-empty">
          No rule firings recorded for this claim yet.
        </div>
      ) : (
        <table className="rules-firings-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Phase</th>
              <th>Rule</th>
              <th>Action</th>
              <th>Field</th>
              <th>Before → After</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {firings.map((f) => (
              <tr key={f.id} className={`firing-${f.outcome}`}>
                <td>
                  {f.fired_at ? f.fired_at.slice(0, 16).replace("T", " ") : "-"}
                </td>
                <td>{f.phase}</td>
                <td>
                  {f.rule_key} v{f.rule_version}
                </td>
                <td>{f.action_type}</td>
                <td>{f.field || "-"}</td>
                <td>
                  {f.before_value != null || f.after_value != null
                    ? `${f.before_value ?? "∅"} → ${f.after_value ?? "∅"}`
                    : "-"}
                </td>
                <td>{f.outcome}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function commentPreview(body) {
  const text = body || "";
  return text.length > COMMENT_PREVIEW_LENGTH
    ? text.slice(0, COMMENT_PREVIEW_LENGTH).trimEnd() + "…"
    : text;
}

// The hover text. Native `title` rather than a custom tooltip: it is one line of markup,
// it respects the reader's own settings, and the row is dense enough already.
function commentTooltip(last, count) {
  if (!last) return "";
  const when = last.created_at
    ? new Date(last.created_at).toLocaleString()
    : "";
  const who = last.author_name || "Unknown";
  const head = count > 1 ? `Last of ${count} comments — ${who}` : who;
  return `${head}${when ? ` · ${when}` : ""}
${last.body || ""}`;
}

const CLAIM_STATUSES = [
  "draft",
  "submitted",
  "accepted",
  "rejected",
  "denied",
  "paid",
  "partial",
  "appealed",
  "patient",
  "credit",
  // A superseded original: PracticeSuite rebilled the claim under a new Claim# and
  // closed this one (David, 2026-10-19). Not open, not in flight; the visit is read
  // from its successor.
  "rebilled",
];
// When Stedi is enabled, any claim can be selected for (re)submission: a biller may
// deliberately re-file. Selecting one is manual; sending one again asks first.
const SUBMITTABLE_STATUSES = CLAIM_STATUSES;
// A claim the payer has never had (draft) or one that came back unaccepted (rejected).
// Mirrors claim_submission_service.FIRST_SUBMISSION_STATUSES: the server skips anything
// else, reporting it as already_submitted, unless the request carries resubmit: true.
// "Select all clean" picks only these (2026-09-22: it picked every clean claim on the
// page, and 48 claims went to the payer up to four times each).
export const FIRST_SUBMISSION_STATUSES = ["draft", "rejected"];

export function resendPromptText(resendCount, total) {
  const were = resendCount === 1 ? "was" : "were";
  return (
    `${formatNumber(resendCount)} of the ${formatNumber(total)} selected claims ${were} already submitted; ` +
    "sending them again creates duplicate claims at the payer. Send anyway?"
  );
}

// Sentinel CPT for a header-only claim's single synthetic charge line (mirrors
// claim_line_backfill_service.HEADER_ONLY_CPT). Header-only sources (eCW/Alleviate)
// carry no procedure codes, so this token anchors the claim-level charge; it is not
// a real CPT and is shown as an em dash rather than the literal "CLAIM".
const HEADER_ONLY_CPT = "CLAIM";

// Close a filter panel on an outside click or Escape — a panel that can only be
// dismissed by re-clicking the button is the usual complaint about hand-rolled dropdowns.
function useDismiss(open, setOpen, ref) {
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, ref, setOpen]);
}

// The dropdown caret on every filter toggle, filtered or not, so a set filter still
// reads as something that opens (David, 2026-10-21).
const Caret = () => (
  <span className="filter-caret" aria-hidden="true">
    ▾
  </span>
);

/**
 * Status filter: checkboxes, so several statuses can be on at once.
 *
 * A single <select> could only ask one question at a time, and the questions a
 * biller actually has are plural — "what has been denied OR rejected", "what is
 * still submitted OR accepted". The value stays a comma-separated string rather
 * than an array so it drops into the existing `filters` object, the query string,
 * and the "any filter set?" checks unchanged; the route splits it and uses IN.
 */
export function StatusFilter({ value, onChange, clientView = false }) {
  const STATUS_GROUPS = statusGroups(clientView);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = value ? value.split(",").filter(Boolean) : [];

  useDismiss(open, setOpen, ref);

  // One checkbox per simplified status (utils/claimStatus.js). A group is ticked when
  // every status in it is selected, and shows as part-ticked when only some are — a
  // dashboard click can select `partial` alone, which sits inside Submitted.
  const groupState = (group) => {
    const n = group.statuses.filter((s) => selected.includes(s)).length;
    return n === 0 ? "none" : n === group.statuses.length ? "all" : "some";
  };
  const emit = (next) =>
    // Emitted in CLAIM_STATUSES order, not click order, so the same set of
    // statuses always produces the same query string.
    onChange(CLAIM_STATUSES.filter((s) => next.includes(s)).join(","));

  const toggle = (group) => {
    const next =
      groupState(group) === "all"
        ? selected.filter((s) => !group.statuses.includes(s))
        : [...new Set([...selected, ...group.statuses])];
    emit(next);
  };

  const ticked = STATUS_GROUPS.filter((g) => groupState(g) !== "none");
  const allTicked = STATUS_GROUPS.every((g) => groupState(g) === "all");
  const label =
    ticked.length === 0
      ? "All Statuses"
      : ticked.length === 1
        ? ticked[0].label
        : `${ticked.length} statuses`;

  return (
    <div className="status-filter" ref={ref}>
      <button
        type="button"
        className={`status-filter-toggle${selected.length ? " is-active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {label}
        <Caret />
      </button>
      {open && (
        <div
          className="status-filter-panel"
          role="group"
          aria-label="Filter by status"
        >
          {/* Listed alphabetically (David, 2026-10-21). */}
          {STATUS_GROUPS.map((g) => (
            <label key={g.key} className="status-filter-option">
              <input
                type="checkbox"
                checked={groupState(g) === "all"}
                ref={(el) => {
                  if (el) el.indeterminate = groupState(g) === "some";
                }}
                onChange={() => toggle(g)}
              />
              <span>{g.label}</span>
            </label>
          ))}
          <div className="status-filter-footer">
            {!allTicked && (
              <button
                type="button"
                className="btn-link status-filter-clear"
                onClick={() => emit(STATUS_GROUPS.flatMap((g) => g.statuses))}
              >
                Select All
              </button>
            )}
            {selected.length > 0 && (
              <button
                type="button"
                className="btn-link status-filter-clear"
                onClick={() => onChange("")}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Payer filter: the same checkbox dropdown as StatusFilter, over the payer names
 * actually present on this practice's claims.
 *
 * The value is an ARRAY, not the comma-separated string StatusFilter uses. Statuses
 * are a fixed enum and can never contain a comma; payer names come out of vendor
 * exports and routinely do ("Blue Cross Blue Shield, Inc."), so joining them would
 * produce a filter that silently matches nothing. The array is sent as a repeated
 * query param and the route reads it with getlist.
 */
export function PayerFilter({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = value || [];

  useDismiss(open, setOpen, ref);

  const toggle = (payer) => {
    const next = selected.includes(payer)
      ? selected.filter((p) => p !== payer)
      : [...selected, payer];
    // Emitted in `options` order, not click order, so the same set of payers always
    // produces the same query string (and the same React key order).
    const ordered = options.filter((p) => next.includes(p));
    // Every payer ticked IS no payer filter, so it is sent as none. Enumerating the names
    // puts one `payer=` per name on the request line, which past ~90 payers exceeds
    // gunicorn's 4094-byte limit and fails the list and the export alike. It also keeps
    // claims with no payer name, which "all payers" means and no checkbox can offer.
    onChange(ordered.length === options.length ? [] : ordered);
  };

  const label =
    selected.length === 0
      ? "All Payers"
      : selected.length === 1
        ? selected[0]
        : `${selected.length} payers`;

  return (
    <div className="status-filter" ref={ref}>
      <button
        type="button"
        className={`status-filter-toggle${selected.length ? " is-active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        disabled={options.length === 0}
        title={
          options.length === 0
            ? "No payers on this practice’s claims yet"
            : undefined
        }
      >
        {label}
        <Caret />
      </button>
      {open && (
        <div
          className="status-filter-panel status-filter-panel-wide"
          role="group"
          aria-label="Filter by payer"
        >
          {options.map((p) => (
            <label key={p} className="status-filter-option">
              <input
                type="checkbox"
                checked={selected.includes(p)}
                onChange={() => toggle(p)}
              />
              <span>{p}</span>
            </label>
          ))}
          {/* Select all is "every payer", sent as NO payer filter (see toggle), which is
              also what Clear has always done — so there is one button, named for what it
              means. Unticked boxes with "All Payers" on the toggle is that state. */}
          {selected.length > 0 && (
            <button
              type="button"
              className="btn-link status-filter-clear"
              onClick={() => onChange([])}
            >
              Select all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Provider filter: the same checkbox dropdown, over this practice's providers, so
 * several can be ticked at once (David, 2026-10-21). The value is an array of provider
 * ids as strings, sent as one repeated provider_id each.
 */
export function ProviderFilter({ value, providers, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = value || [];
  useDismiss(open, setOpen, ref);

  const toggle = (id) => {
    const next = selected.includes(id)
      ? selected.filter((p) => p !== id)
      : [...selected, id];
    // List order, not click order, so the same picks make the same query string.
    onChange(providers.map((p) => String(p.id)).filter((p) => next.includes(p)));
  };

  const picked = providers.filter((p) => selected.includes(String(p.id)));
  const label =
    picked.length === 0
      ? "All Providers"
      : picked.length === 1
        ? picked[0].name
        : `${picked.length} providers`;

  return (
    <div className="status-filter" ref={ref}>
      <button
        type="button"
        className={`status-filter-toggle${picked.length ? " is-active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {label}
        <Caret />
      </button>
      {open && (
        <div
          className="status-filter-panel status-filter-panel-wide"
          role="group"
          aria-label="Filter by provider"
        >
          {providers.map((p) => (
            <label key={p.id} className="status-filter-option">
              <input
                type="checkbox"
                checked={selected.includes(String(p.id))}
                onChange={() => toggle(String(p.id))}
              />
              <span>{p.name}</span>
            </label>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              className="btn-link status-filter-clear"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// The filter set survives sidebar navigation (Claims -> Dashboard -> Claims) but NOT a
// browser refresh. It lives in a module-level variable rather than sessionStorage
// deliberately: sessionStorage outlives a reload, and a reload is the escape hatch a
// user reaches for when a filter set has them confused about what they are looking at.
//
// The three import buckets are deliberately NOT carried across. They are scoped to an
// upload observed in this page instance (see uploadedThisSession), so restoring
// `created: '1'` after a tab change would narrow the ledger to the last import's new
// rows with no pill on screen to explain why, or to switch it back off.
// Built fresh each time rather than shared as a constant: `payer` is an array, and a
// shared one would be aliased into state by every caller that spread it.
function defaultFilters() {
  return {
    status: "",
    date_from: "",
    date_to: "",
    provider_ids: [],
    payer: [],
    patient: "",
    // One patient's claims by id, set only by a click from the Patients page. The
    // Patient box shows their name; typing in it drops back to the name search.
    patient_id: "",
    // "insurance" | "patient" | "": an A/R amount's claims, the ones still carrying a
    // balance (routes/ledger.py `balance`). Set only by a click on the A/R page.
    balance: "",
    created: "",
    updated: "",
    flagged: "",
  };
}

// The ledger's filters and sort as a query string. ONE builder for the list request and
// the CSV export, so "Export CSV" downloads exactly what the filter bar selects: the
// server runs both through the same query (routes/ledger.claims_list_query) and this makes
// sure they are asked the same question. Pagination is added by the list request only.
export function ledgerQueryParams(filters, sort) {
  const params = new URLSearchParams();
  params.set("sort", sort.field);
  params.set("order", sort.order);
  if (filters.status) params.set("status", filters.status);
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  // Repeated, one per provider, like payer below.
  (filters.provider_ids || []).forEach((id) => params.append("provider_id", id));
  // One repeated param per payer rather than a joined value: payer names can
  // contain commas, so they cannot be packed the way statuses are.
  (filters.payer || []).forEach((p) => params.append("payer", p));
  // An exact patient wins over the name: the name on a claim is spelled however the
  // EMR exported it, so matching it as text could miss this patient's claims.
  if (filters.patient_id) params.set("patient_id", filters.patient_id);
  else if (filters.patient) params.set("patient", filters.patient);
  if (filters.balance) params.set("balance", filters.balance);
  if (filters.created) params.set("created", "1");
  if (filters.updated) params.set("updated", "1");
  if (filters.flagged) params.set("flagged", "1");
  return params;
}

// Both stores below are keyed by practice and MUST stay that way.
//
// The page does not unmount on a practice switch. App.js picks the page with a switch
// on activePage (App.js:468), so changing practice re-renders this same component with
// a new prop — only sidebar navigation unmounts it. Anything held across renders
// therefore outlives the practice it was loaded for unless it is explicitly keyed or
// cleared. The pre-existing importStatus effect below clears itself for exactly this
// reason; these two need the same discipline, and the practiceId key is what enforces
// it on the mount path.
let rememberedFilters = null; // { practiceId, filters }

// The last ledger this page rendered, so returning to it paints immediately instead of
// flashing an empty table, empty stat headers and a spinner while the same four
// requests run again.
//
// This does NOT skip the refetch. Every mount still loads fresh data; the cache only
// decides whether the user watches that happen.
let ledgerCache = null; // { practiceId, claims, ... }

function readCache(practiceId) {
  if (!practiceId || !ledgerCache || ledgerCache.practiceId !== practiceId)
    return null;
  return ledgerCache;
}

// Filters restore only for the practice they were set on. A provider or payer id is
// meaningless against another practice: the route filters a practice-scoped query by
// it, so a carried-over value returns an empty page while the <select> shows nothing
// selected — a ledger that reads "No claims found." for a practice that has claims.
function restoreFilters(practiceId) {
  const base = defaultFilters();
  // A dashboard chart click wins over the remembered set: the person asked for those claims.
  const linked = peekLedgerLink(practiceId);
  if (linked) {
    return {
      ...base,
      ...linked,
      payer: [...(linked.payer || [])],
      provider_ids: [...(linked.provider_ids || [])],
      created: "",
      updated: "",
      flagged: "",
    };
  }
  if (
    !practiceId ||
    !rememberedFilters ||
    rememberedFilters.practiceId !== practiceId
  ) {
    return base;
  }
  const f = rememberedFilters.filters || {};
  return {
    ...base,
    ...f,
    // Copied, not aliased: the remembered object is handed straight back to state.
    payer: [...(f.payer || [])],
    provider_ids: [...(f.provider_ids || [])],
    created: "",
    updated: "",
    flagged: "",
  };
}

// Common CARC code descriptions for rejection analysis
const CARC_DESCRIPTIONS = {
  "CO-4": "The procedure code is inconsistent with the modifier used",
  "CO-45": "Charge exceeds fee schedule/maximum allowable",
  "CO-97":
    "The benefit for this service is included in the payment/allowance for another service",
  "CO-16": "Claim/service lacks information needed for adjudication",
  "CO-18": "Exact duplicate claim/service",
  "CO-29": "The time limit for filing has expired",
  "CO-50": "Non-covered service (not deemed a medical necessity)",
  "PR-1": "Deductible amount",
  "PR-2": "Coinsurance amount",
  "PR-3": "Copayment amount",
  "OA-23":
    "The impact of prior payer(s) adjudication including payments and/or adjustments",
};

function formatNumber(val) {
  return Number(val).toLocaleString("en-US");
}

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}

// Claim statuses Instance F's editor refuses with a 409 — the payer has adjudicated
// them, so a content change is a corrected claim rather than an edit. Mirrors
// `claim_edit_service.REFUSED_STATUSES`; if F's list moves, this moves with it.
const EDITOR_REFUSES = [
  "submitted",
  "accepted",
  "paid",
  "partial",
  "denied",
  "patient",
  "appealed",
  "credit",
  "rebilled",
];

function ClaimsLedgerPage({
  selectedPractice,
  readOnly,
  isAdmin,
  onPageChange,
  // Set when this ledger is a claim list's body (features/claimLists/ClaimListPage): the
  // rows are narrowed to the list's claims, and the page keeps none of the Claims page's
  // memory (cache, remembered filters, the drawer's URL), which belongs to that page.
  claimListId = null,
  onOpenList,
  onListsChanged,
}) {
  const listMode = Boolean(claimListId);
  // Whatever this page last rendered for this practice, or null on a cold start. Read
  // once, during render, so the useState initialisers below all see the same snapshot.
  const cached = listMode ? null : readCache(selectedPractice?.id);

  // The claim editor opens as a drawer OVER this page (David's walk, 2026-09-15:
  // "claim editor as a pop-up from the claims page"). The /p/<id>/claims/<claimId> deep
  // link still works: App.js parses it, names the claim, and this reads it once on mount
  // so a bookmark lands on the ledger with the drawer already open.
  const [editorClaimId, setEditorClaimId] = useState(() =>
    listMode ? null : readClaimEditorTarget(selectedPractice?.id),
  );
  // Whether the open editor has typing that has not been saved. A drawer closes far
  // more easily than a page did — a click anywhere on the backdrop — so closing asks
  // first when there is something to lose.
  const [editorDirty, setEditorDirty] = useState(false);

  // The capability gate. Read here rather than threaded from App.js: the hook caches
  // per practice for the life of the tab, so asking for it in a second place costs
  // nothing, and App.js belongs to another strand this sprint.
  const { features } = useCapabilities(selectedPractice?.id);
  // Brief 15: a portal practice gets "Correct" only when its runbook covers the
  // contract's `corrections` feature; a Stedi practice is unaffected (its gate is
  // open and the clearinghouse check below still decides).
  const { gates: pmsGates } = usePmsGates(selectedPractice?.id);
  const correctionsCovered =
    pmsGates.applicable && pmsGates.corrections.allowed;

  // {claimId: {followup, overdue}} for the practice, fetched ONCE per practice rather
  // than per row — a chip that fetched per row would be fifty round trips on a full
  // page. Empty when the worklist feature is off (the endpoint 404s), so the chips
  // simply do not appear.
  const [followupMap, setFollowupMap] = useState({});

  const [claims, setClaims] = useState(() => cached?.claims || []);
  const [claimsTotal, setClaimsTotal] = useState(
    () => cached?.claimsTotal || 0,
  );
  const [claimsPage, setClaimsPage] = useState(() => cached?.claimsPage || 1);
  const [claimsPages, setClaimsPages] = useState(
    () => cached?.claimsPages || 1,
  );
  const [loading, setLoading] = useState(false);
  // Which practice the rows currently in state were actually loaded for. The cache is
  // written only while this matches the selected practice, which is what stops a
  // practice switch from filing the outgoing practice's claims under the incoming
  // practice's id during the render before the reset effect lands.
  const [dataPracticeId, setDataPracticeId] = useState(
    () => cached?.practiceId ?? null,
  );
  // Ticket for the newest in-flight claims request; see loadClaims.
  const claimsRequestRef = useRef(0);
  // Lazy initialiser: restores the filter set left behind by the previous mount, so
  // navigating away to another page and back does not silently reset the view. Returns
  // defaults when the memory belongs to a different practice.
  const [filters, setFilters] = useState(() =>
    listMode ? defaultFilters() : restoreFilters(selectedPractice?.id),
  );
  // The dashboard's hand-off has been read into state; drop it so a later visit does not
  // re-apply it.
  useEffect(() => {
    if (!listMode) clearLedgerLink();
  }, [listMode]);
  // Claims ticked for a claim list ("Create list" / "Add to list…"). Its own set, apart
  // from the submission selection below, so a list can hold any claim (paid, denied,
  // blocked by rules) without a single one of them ever reaching Submit.
  const [listSelectedIds, setListSelectedIds] = useState(() => new Set());
  // Bumped after a list is created or added to, so the Lists strip re-reads.
  const [listsVersion, setListsVersion] = useState(0);
  const [sort, setSort] = useState({ field: "date_of_service", order: "desc" });
  const [expandedClaimId, setExpandedClaimId] = useState(null);
  const [expandedClaimLines, setExpandedClaimLines] = useState(null); // null = not loaded, [] = loaded empty
  // The rows that make the line table foot to the claim row: a "Claim-level
  // (unallocated)" row when money could not be placed on a line, and TOTAL.
  const [expandedClaimFooting, setExpandedClaimFooting] = useState(null);
  const [claimTimeline, setClaimTimeline] = useState({}); // { [claimId]: [events] }
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  // Which side of the right-hand panel a claim is showing. Keyed by claim rather than shared,
  // so opening a second claim does not inherit the first one's tab. Absent = 'comments', which
  // is the default because it is the half a person can act on.
  const [detailTab, setDetailTab] = useState({}); // { [claimId]: 'comments' | 'history' }
  // An ABSENT key means not loaded. It must never be set to [] on failure, or a claim whose
  // comments could not be fetched renders the empty state and tells the reader there are none.
  const [claimComments, setClaimComments] = useState({}); // { [claimId]: [comments] }
  const [commentLoadError, setCommentLoadError] = useState({}); // { [claimId]: message }
  // Drafts, errors and busy flags are all keyed by claim: one shared value put claim A's
  // error under claim B and locked B's Add button while A was still posting.
  const [commentDrafts, setCommentDrafts] = useState({}); // { [claimId]: text }
  const [postingCommentFor, setPostingCommentFor] = useState(null); // claimId
  const [commentError, setCommentError] = useState({}); // { [claimId]: message }
  // Bumped on every load and every write, so a slow list GET that lands after a comment was
  // posted is discarded rather than overwriting the pane with a snapshot taken before it.
  const commentSeq = useRef({});
  const [loadingClaimLines, setLoadingClaimLines] = useState(false);

  // Providers for filter dropdown
  const [providers, setProviders] = useState(() => cached?.providers || []);
  // Payer names for the payer filter dropdown, from this practice's own claims.
  const [payerOptions, setPayerOptions] = useState(
    () => cached?.payerOptions || [],
  );
  // Whether an import finished while THIS page instance was mounted. Gates the
  // New / Updated / Flagged pills, whose counts are practice-level and would
  // otherwise show every user the result of somebody else's upload, indefinitely.
  // Component state, not the module-level store above, precisely so it dies on a
  // tab change the way the filter set does not.
  const [uploadedThisSession, setUploadedThisSession] = useState(false);

  // Add/Edit modal
  const [showForm, setShowForm] = useState(false);
  const [editingClaim, setEditingClaim] = useState(null);
  const [editingPatient, setEditingPatient] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [uploadFormat, setUploadFormat] = useState("");
  const [excludedRows, setExcludedRows] = useState(new Set());
  // In-app async import status chip (Amber running / Green done). Red waits on the
  // last_import_error column (David approval), so there's no error state here yet.
  const [importStatus, setImportStatus] = useState(null); // { status, last_imported_at } | null
  // Green "done" is transient: only shown after a completion OBSERVED this session, not for a
  // stale past import (last_imported_at persists as the dashboard's "last refreshed at").
  const [importDoneThisSession, setImportDoneThisSession] = useState(false);
  const importRefreshedRef = useRef(false); // guards the running->success ledger refresh to exactly once
  const [asyncFile, setAsyncFile] = useState(null); // file chosen for the async "Auto" import; import waits for the Start Import click
  const [staleConfirm, setStaleConfirm] = useState(null); // {file, error, fileThrough, importedThrough} for the in-modal older-file warning
  // After a successful upload, if the import created stub payers (no
  // payer_id), surface a small modal pointing the user at the Practice
  // Details page where they can fill in the IDs.
  const [pendingPayerNotice, setPendingPayerNotice] = useState(null);

  // Submit / Retry state
  const [submitting, setSubmitting] = useState(false);
  const [retryingClaimId, setRetryingClaimId] = useState(null);
  // Corrected-claim dialog (B2): drafts a NEW claim (frequency 7/8) linked to an
  // adjudicated original. The draft is then edited/submitted like any other.
  const [correcting, setCorrecting] = useState(null); // { claim, info, frequency, pccn, reason, busy, error }
  const [checkingStatusId, setCheckingStatusId] = useState(null);
  const [statusModal, setStatusModal] = useState(null); // { title, message, confirmLabel?, onConfirm? }
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text: '...' }

  // Checkbox selection state
  const [selectedClaimIds, setSelectedClaimIds] = useState(new Set());
  // Every status this page has seen, by claim id, so Submit can tell which selected
  // claims were already sent even when "select all matching" picked them on another
  // page. An id missing from it is treated as already sent: the prompt asks rather
  // than guesses.
  const claimStatusByIdRef = useRef(new Map());
  // The "already submitted — send anyway?" dialog: {count, total, resolve}.
  const [resendPrompt, setResendPrompt] = useState(null);
  // Has Atlas submitted this practice's claims in the last week
  // (GET /submission-status). Only level 'alarm' renders anything.
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [selectingAll, setSelectingAll] = useState(false);

  function emptyForm() {
    return {
      date_of_service: "",
      patient_name: "",
      rendering_provider_id: "",
      rendering_provider_name: "",
      payer_name: "",
      payer_id: "",
      member_id: "",
      cpt_codes: [{ code: "", modifiers: "", units: 1, charge: "" }],
      icd_codes: [""],
      billed_amount: "",
      claim_status: "draft",
    };
  }

  const practiceId = selectedPractice?.id;
  // Only practices explicitly opted in (DB flag) use the validated async /import-file pipeline.
  // Everyone else stays on the legacy preview/confirm upload (the old import route). Fail-closed:
  // absent/false -> legacy, so a practice is never moved onto the new path until it's verified.
  const inappImportEnabled = !!selectedPractice?.inapp_import_enabled;

  // Load providers for filter dropdown.
  //
  // The cancelled flag is load-bearing, not boilerplate. Without it a slow fetch for
  // the previous practice resolves after a switch and writes that practice's clinicians
  // into state; the ledger cache then persists them, because its dataPracticeId guard
  // only certifies the CLAIMS. Practice B's provider filter would list practice A's
  // clinicians, and picking one sends a foreign provider_id into a practice-scoped
  // query — an empty ledger for a practice that has claims.
  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/practices/${practiceId}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.providers && !cancelled) setProviders(data.providers);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  // Payer names for the filter dropdown. Its own claims-scoped route rather than the
  // dashboard's, which is admin-only and would 403 the billers this filter is for.
  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/practices/${practiceId}/claims/payers`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.payers || cancelled) return;
        setPayerOptions(data.payers);
        // Drop any selected payer this practice does not have. The filter set outlives
        // both navigation and a practice switch, but payer names are practice-scoped
        // data — carried across, one would filter the ledger to an empty page while
        // the dropdown showed no box to untick, since it lists only this practice's
        // payers. Returning `prev` unchanged when there is nothing to prune keeps this
        // from re-rendering in a loop.
        setFilters((prev) => {
          if (!prev.payer.length) return prev;
          const kept = prev.payer.filter((p) => data.payers.includes(p));
          return kept.length === prev.payer.length
            ? prev
            : { ...prev, payer: kept };
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  // Hand the current filter set to the module-level store so the next mount can restore
  // it. Written on every change rather than on unmount: an unmount cleanup would capture
  // a stale closure unless it were re-registered on each change anyway.
  // Drop everything scoped to the previous practice the moment the prop changes.
  //
  // This page is NOT unmounted by a practice switch (App.js:468 keeps it mounted and
  // swaps the prop), so without this, claims, providers and payer options all
  // survive into a practice they do not describe — and the cache effect below then
  // stores one practice's rows under another practice's id. In a PHI application that
  // is patient names and charges rendered under the wrong practice's header.
  //
  // Guarded by a ref rather than running on mount, because on mount the state was just
  // hydrated from the cache for THIS practice and must not be cleared.
  //
  // provider_ids and payer go with it: both are ids that only mean something within the
  // practice they were chosen in, and the route filters a practice-scoped query by
  // them, so a carried-over value silently empties the ledger.
  const knownPracticeIdRef = useRef(practiceId);
  useEffect(() => {
    if (knownPracticeIdRef.current === practiceId) return;
    knownPracticeIdRef.current = practiceId;
    setClaims([]);
    setClaimsTotal(0);
    setClaimsPage(1);
    setClaimsPages(1);
    setProviders([]);
    setPayerOptions([]);
    setDataPracticeId(null);
    // provider_ids and payer are practice-scoped ids; the three import buckets are
    // scoped to an upload observed in this page instance, and the switch below clears
    // uploadedThisSession, so leaving one set would narrow the incoming practice's
    // ledger to its last import's rows with every pill gone from the toolbar — no way
    // to see why, and nothing to click to undo it. restoreFilters already drops the
    // buckets on the mount path for exactly this reason; this path is the same hazard
    // reached by switching practice without leaving the page.
    //
    // `prev` unchanged when there is nothing to clear. Returning a fresh object
    // unconditionally changed the filters identity on every switch, which rebuilt
    // loadClaims and fired a second claims request racing the first — with different
    // filters, so the loser could settle the page on the wrong result. Both halves
    // must list the same fields or that double request comes back.
    setFilters((prev) =>
      prev.provider_ids.length ||
      prev.payer.length ||
      prev.created ||
      prev.updated ||
      prev.flagged
        ? {
            ...prev,
            provider_ids: [],
            payer: [],
            created: "",
            updated: "",
            flagged: "",
          }
        : prev,
    );
  }, [practiceId]);

  // On the render where the practice prop changes, this effect still closes over the
  // OUTGOING practice's filters while `practiceId` is already the incoming one, so it
  // briefly stores the wrong pair. Being declared after the reset does not prevent that
  // — effects in one commit all observe the state as it was before that commit's
  // updates, not the values the reset just queued.
  //
  // It is safe because the reset's setFilters forces a re-render in the same commit
  // cycle, this effect runs again with the cleared filters and overwrites, and all of
  // that happens before the browser paints. The only reader is the useState initialiser
  // on mount, and a mount cannot interleave between those two renders. If a reader is
  // ever added elsewhere, this needs the same practice stamp `dataPracticeId` gives the
  // ledger cache.
  useEffect(() => {
    if (!practiceId || listMode) return;
    rememberedFilters = { practiceId, filters };
  }, [practiceId, filters, listMode]);

  // Snapshot what is on screen for the next mount (see ledgerCache). Written on change
  // rather than in an unmount cleanup, which would capture a stale closure unless it
  // were re-registered on every change anyway.
  //
  // The dataPracticeId guard is the load-bearing part. On the render where the practice
  // prop changes, this effect still closes over the OUTGOING practice's rows while
  // `practiceId` is already the incoming one; dataPracticeId is likewise still the
  // outgoing id, so the write is skipped rather than mislabelling those rows. It stays
  // skipped until a response for the new practice actually lands.
  useEffect(() => {
    if (!practiceId || dataPracticeId !== practiceId || listMode) return;
    ledgerCache = {
      practiceId,
      claims,
      claimsTotal,
      claimsPage,
      claimsPages,
      providers,
      payerOptions,
    };
  }, [
    practiceId,
    dataPracticeId,
    claims,
    claimsTotal,
    claimsPage,
    claimsPages,
    providers,
    payerOptions,
    listMode,
  ]);

  // One request per practice for every follow-up, so the row chips cost nothing per
  // row. Re-run when the worklist feature flips on (the endpoint 404s while it is off
  // and fetchFollowupMap answers {}).
  useEffect(() => {
    let cancelled = false;
    if (!selectedPractice?.id || !features.worklist) {
      setFollowupMap({});
      return undefined;
    }
    fetchFollowupMap(selectedPractice.id).then((map) => {
      if (!cancelled) setFollowupMap(map);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedPractice?.id, features.worklist]);

  const loadClaims = useCallback(
    async (page = 1) => {
      if (!practiceId) return;
      // Every request takes a ticket; only the newest one may write. Claims requests are
      // fired by practice switches, filter changes, sorts and pagination, none of which
      // wait for the one before, and responses can arrive in any order.
      //
      // Two things this stops. A response for the PREVIOUS practice landing after a
      // switch would otherwise set claims and dataPracticeId together — the cache write
      // is correctly skipped, but the render guard consults only `loading` and
      // `claims.length`, so the outgoing practice's patient names and charges would paint
      // under the incoming practice's header and stay there until something else
      // refetched. And within one practice, a slow unfiltered request resolving after a
      // fast filtered one would leave the table disagreeing with the filter bar.
      const ticket = ++claimsRequestRef.current;
      setLoading(true);
      try {
        const params = ledgerQueryParams(filters, sort);
        params.set("page", page);
        params.set("per_page", 50);
        if (claimListId) params.set("claim_list_id", claimListId);

        const response = await fetch(
          `${API_URL}/api/practices/${practiceId}/claims?${params}`,
          { credentials: "include" },
        );
        if (ticket !== claimsRequestRef.current) return; // superseded — drop it
        if (response.ok) {
          const data = await response.json();
          // Checked AGAIN, because parsing the body is a second suspension point: a
          // practice switch or a filter change during that await issues a newer ticket,
          // and one check before it would let this stale response write anyway. The
          // check that matters is the one immediately before the state write.
          if (ticket !== claimsRequestRef.current) return;
          setClaims(data.claims || []);
          setClaimsTotal(data.total || 0);
          setClaimsPage(data.page || 1);
          setClaimsPages(data.pages || 1);
          // Stamp the rows with the practice they were fetched for, so the cache write
          // can tell whose they are.
          setDataPracticeId(practiceId);
        }
      } catch (err) {
        // Silently fail
      } finally {
        // Only the newest request owns the spinner. A superseded one clearing it would
        // uncover an empty table while the request that matters is still running.
        if (ticket === claimsRequestRef.current) setLoading(false);
      }
    },
    [practiceId, sort, filters, claimListId],
  );

  useEffect(() => {
    loadClaims(1);
  }, [loadClaims]);

  // A deep-linked claim is consumed once: reopening the ledger later must not reopen
  // somebody's last claim over it.
  useEffect(() => {
    if (editorClaimId) clearClaimEditorTarget();
  }, [editorClaimId]);

  // The drawer is a place, so it has a URL: opening one pushes /p/<id>/claims/<claim>,
  // closing it goes back to /p/<id>/claims. That makes the deep link something a biller
  // can copy out of the address bar as well as arrive on, and Back closes the drawer
  // instead of leaving the ledger. App.js mirrors the URL only when the page or the
  // practice changes, so it does not fight this.
  const urlClaimRef = useRef(null);
  useEffect(() => {
    // A claim list's page has its own URL (/p/<id>/lists/<list>); the drawer opens over
    // it without moving the address bar.
    if (!practiceId || listMode) return;
    if (urlClaimRef.current === editorClaimId) return;
    urlClaimRef.current = editorClaimId;
    const base = `/p/${practiceId}/claims`;
    const target = editorClaimId ? `${base}/${editorClaimId}` : base;
    if (window.location.pathname === target) return;
    if (editorClaimId) {
      window.history.pushState({}, "", target);
    } else {
      window.history.replaceState({}, "", target);
    }
  }, [editorClaimId, practiceId, listMode]);

  // Back/forward: follow the URL rather than stranding the drawer open — through the
  // same guard as every other way out. Back is a door too, and typing must not fall
  // through it silently; refusing puts the claim's URL back so the two agree again.
  useEffect(() => {
    if (listMode) return undefined;
    const onPop = () => {
      const parts = window.location.pathname.split("/").filter(Boolean);
      const parsed =
        parts[2] === "claims" && parts[3] ? parseInt(parts[3], 10) : null;
      const next = Number.isNaN(parsed) ? null : parsed;
      if (next === editorClaimId) return;
      if (
        editorClaimId &&
        !next &&
        editorDirty &&
        !window.confirm(
          "This claim has edits you have not saved. Close the editor and lose them?",
        )
      ) {
        urlClaimRef.current = editorClaimId;
        window.history.pushState(
          {},
          "",
          `/p/${practiceId}/claims/${editorClaimId}`,
        );
        return;
      }
      urlClaimRef.current = next;
      setEditorClaimId(next);
      if (!next) setEditorDirty(false);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [editorClaimId, editorDirty, practiceId, listMode]);

  const closeEditorDrawer = useCallback(() => {
    if (
      editorDirty &&
      !window.confirm(
        "This claim has edits you have not saved. Close the editor and lose them?",
      )
    ) {
      return;
    }
    setEditorDirty(false);
    setEditorClaimId(null);
  }, [editorDirty]);

  // Escape closes it, through the same guard — a drawer that traps you is worse than
  // one that asks.
  useEffect(() => {
    if (!editorClaimId) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeEditorDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editorClaimId, closeEditorDrawer]);

  // Never leave one practice's claim open over another practice's ledger. Skips the
  // first run: on mount the open claim is the deep link's, and closing it here would
  // undo the thing the URL asked for.
  const drawerPracticeRef = useRef(practiceId);
  useEffect(() => {
    if (drawerPracticeRef.current === practiceId) return;
    drawerPracticeRef.current = practiceId;
    setEditorClaimId(null);
  }, [practiceId]);

  // Fetch the current import status once per practice, so the chip reflects a run that
  // finished or is still going (possibly started from another session). Reset the session
  // "done" flags on a practice switch — a stale past import must not show a green chip.
  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    // Clear the previous practice's chip immediately (the fetch below repopulates for the new one),
    // so a stale running/error/stalled chip never flashes attributed to the wrong practice.
    setImportStatus(null);
    setImportDoneThisSession(false);
    // The pills belong to an upload done here, for this practice. Switching practice
    // makes them somebody else's counts again.
    setUploadedThisSession(false);
    importRefreshedRef.current = false;
    fetch(`${API_URL}/api/practices/${practiceId}/claims/import-status`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && !cancelled) setImportStatus(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  // While an import is running, poll every 4s until it settles; refresh the ledger once it
  // completes. Polling runs ONLY in the running state (this effect unmounts otherwise). The ref
  // guard makes the running->success refresh fire exactly once even if two polls overlap on a
  // slow response.
  useEffect(() => {
    if (importStatus?.status !== "running") return;
    const id = setInterval(() => {
      fetch(`${API_URL}/api/practices/${practiceId}/claims/import-status`, {
        credentials: "include",
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data) return;
          if (data.status === "success" && !importRefreshedRef.current) {
            importRefreshedRef.current = true;
            setImportDoneThisSession(true);
            setUploadedThisSession(true);
            loadClaims(1);
          }
          setImportStatus(data);
        })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(id);
  }, [importStatus?.status, practiceId, loadClaims]);

  // Clear selection when filters or sort change
  useEffect(() => {
    setSelectedClaimIds(new Set());
  }, [filters, sort]);
  useEffect(() => {
    setListSelectedIds(new Set());
  }, [filters, sort, practiceId]);

  const toggleListSelection = (claimId) => {
    setListSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId);
      else next.add(claimId);
      return next;
    });
  };
  const allPageListSelected =
    claims.length > 0 && claims.every((c) => listSelectedIds.has(c.id));
  const toggleAllListOnPage = () => {
    setListSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageListSelected) claims.forEach((c) => next.delete(c.id));
      else claims.forEach((c) => next.add(c.id));
      return next;
    });
  };
  const onListWritten = () => {
    setListSelectedIds(new Set());
    setListsVersion((v) => v + 1);
    if (onListsChanged) onListsChanged();
  };

  const handleSort = (field) => {
    setSort((prev) => ({
      field,
      order: prev.field === field && prev.order === "desc" ? "asc" : "desc",
    }));
  };

  // The New / Updated / Flagged import pills are mutually exclusive: toggling one on
  // clears the other two (they partition the last import's changed set).
  const toggleImportFilter = (key) => {
    setFilters((prev) => ({
      ...prev,
      created: "",
      updated: "",
      flagged: "",
      [key]: prev[key] ? "" : "1",
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!editingClaim) {
      setFormError("No claim selected");
      return;
    }
    if (!form.date_of_service) {
      setFormError("Date of service is required");
      return;
    }

    const cptCodes = form.cpt_codes
      .filter((c) => c.code.trim())
      .map((c) => ({
        code: c.code.trim(),
        modifiers: c.modifiers
          ? c.modifiers
              .split(",")
              .map((m) => m.trim())
              .filter(Boolean)
          : [],
        units: parseInt(c.units) || 1,
        charge: parseFloat(c.charge) || 0,
      }));
    const icdCodes = form.icd_codes.filter((c) => c.trim());
    const billedAmount =
      form.billed_amount ||
      cptCodes.reduce((sum, c) => sum + c.charge * c.units, 0);

    const payload = {
      date_of_service: form.date_of_service,
      patient_name: form.patient_name,
      rendering_provider_id: form.rendering_provider_id || null,
      rendering_provider_name: form.rendering_provider_name,
      payer_name: form.payer_name,
      payer_id: form.payer_id,
      member_id: form.member_id,
      cpt_codes: cptCodes,
      icd_codes: icdCodes,
      billed_amount: billedAmount,
      claim_status: form.claim_status,
    };

    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${editingClaim.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );
      if (response.ok) {
        setShowForm(false);
        setEditingClaim(null);
        loadClaims(claimsPage);
      } else {
        const data = await response.json();
        setFormError(data.error || "Failed to save claim");
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (claim) => {
    setEditingClaim(claim);
    setEditingPatient(null);
    setForm({
      date_of_service: claim.date_of_service || "",
      patient_name: claim.patient_name || "",
      rendering_provider_id: claim.rendering_provider_id || "",
      rendering_provider_name: claim.rendering_provider_name || "",
      payer_name: claim.payer_name || "",
      payer_id: claim.payer_id || "",
      member_id: claim.member_id || "",
      cpt_codes:
        claim.cpt_codes && claim.cpt_codes.length > 0
          ? claim.cpt_codes.map((c) => ({
              code: c.code,
              modifiers: (c.modifiers || []).join(", "),
              units: c.units,
              charge: c.charge,
            }))
          : [{ code: "", modifiers: "", units: 1, charge: "" }],
      icd_codes:
        claim.icd_codes && claim.icd_codes.length > 0 ? claim.icd_codes : [""],
      billed_amount: claim.billed_amount || "",
      claim_status: claim.claim_status || "draft",
    });
    setFormError(null);
    setShowForm(true);

    if (claim.patient_id) {
      try {
        const response = await fetch(
          `${API_URL}/api/practices/${practiceId}/patients/${claim.patient_id}`,
          { credentials: "include" },
        );
        if (response.ok) {
          setEditingPatient(await response.json());
        }
      } catch (err) {
        // Leave editingPatient null; UI will note that insurance options are unavailable.
      }
    }
  };

  const getInsuranceOptions = () => {
    if (!editingPatient) return [];
    const options = [];
    if (editingPatient.payer_name || editingPatient.payer_id) {
      options.push({
        tier: "primary",
        label: "Primary",
        payer_name: editingPatient.payer_name || "",
        payer_id: editingPatient.payer_id || "",
        member_id: editingPatient.subscriber_id || "",
      });
    }
    if (
      editingPatient.secondary_payer_name ||
      editingPatient.secondary_payer_id
    ) {
      options.push({
        tier: "secondary",
        label: "Secondary",
        payer_name: editingPatient.secondary_payer_name || "",
        payer_id: editingPatient.secondary_payer_id || "",
        member_id: editingPatient.secondary_subscriber_id || "",
      });
    }
    if (
      editingPatient.tertiary_payer_name ||
      editingPatient.tertiary_payer_id
    ) {
      options.push({
        tier: "tertiary",
        label: "Tertiary",
        payer_name: editingPatient.tertiary_payer_name || "",
        payer_id: editingPatient.tertiary_payer_id || "",
        member_id: editingPatient.tertiary_subscriber_id || "",
      });
    }
    return options;
  };

  const getSelectedTier = () => {
    const options = getInsuranceOptions();
    const match = options.find(
      (o) =>
        (o.payer_id || "") === (form.payer_id || "") &&
        (o.member_id || "") === (form.member_id || ""),
    );
    return match ? match.tier : "";
  };

  const handleInsuranceSelect = (tier) => {
    const options = getInsuranceOptions();
    const selected = options.find((o) => o.tier === tier);
    if (selected) {
      setForm({
        ...form,
        payer_name: selected.payer_name,
        payer_id: selected.payer_id,
        member_id: selected.member_id,
      });
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingClaim(null);
    setEditingPatient(null);
  };

  const handleDelete = async (claimId) => {
    if (!window.confirm("Delete this claim?")) return;
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claimId}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      if (response.ok) {
        loadClaims(claimsPage);
      } else {
        alert("Failed to delete claim");
      }
    } catch (err) {
      alert("Failed to delete claim: " + err.message);
    }
  };

  // --- Upload CSV ---

  // Primary async import path: post the raw export to /claims/import-file, which derives
  // source_system from the practice's configured PM/EMR system and reconciles in the
  // background. No preview/confirm — the status chip (Amber -> Green, or Red on failure)
  // reports progress via /claims/import-status polling. A 409 {stale} means the file looks
  // older than data already imported; we confirm and re-send with an override.
  const submitImport = async (file, confirmStale = false) => {
    setUploading(true);
    setUploadError(null);
    setStaleConfirm(null);
    const formData = new FormData();
    formData.append("file", file);
    if (confirmStale) formData.append("confirm_stale", "true");
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/import-file`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        },
      );
      const data = await response.json();
      if (response.status === 202) {
        setShowUploadModal(false);
        setImportDoneThisSession(false); // fresh run: green shows only when THIS import completes
        importRefreshedRef.current = false;
        setImportStatus((prev) => ({
          status: "running",
          last_imported_at: prev?.last_imported_at || null,
        }));
      } else if (response.ok) {
        // 200 no-op: an empty file, or every row skipped by the converter.
        setShowUploadModal(false);
        setMessage({
          type: "success",
          text: "That file had no new rows to import.",
        });
      } else if (response.status === 409 && data.stale) {
        // Show an in-modal warning (not a native browser confirm); its buttons re-submit with the override.
        setStaleConfirm({
          file,
          error: data.error,
          fileThrough: data.file_through,
          importedThrough: data.imported_through,
        });
      } else {
        setUploadError(data.error || "Upload failed");
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Selecting a file no longer auto-starts the import — it just stages the file.
  // The user kicks off the import with the "Start Import" button (submitImport).
  const handleAsyncFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAsyncFile(file);
    setUploadError(null);
    // Clear the input value so re-selecting the SAME file (e.g. after switching
    // format and back) still fires onChange — we hold the file in asyncFile state.
    e.target.value = "";
  };

  // Legacy: session-auth, synchronous parse -> preview -> confirm path (parse_claims_csv via
  // /upload-preview + /upload-confirm) and the PracticeSuite Encounter Report match-merge
  // (/encounter-import). Kept intact; reached only when a specific legacy format is chosen.
  const handleUploadFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setUploadError(null);
    setUploadPreview(null);
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    if (uploadFormat) formData.append("format", uploadFormat);

    // Encounter Report has its own preview shape (summary, not a claim list)
    // and goes to a dedicated endpoint that handles match-update-create logic.
    const isEncounter = uploadFormat === "practicesuite_encounter";
    const url = isEncounter
      ? `${API_URL}/api/practices/${practiceId}/claims/encounter-import`
      : `${API_URL}/api/practices/${practiceId}/claims/upload-preview`;
    if (isEncounter) {
      formData.append("commit", "false");
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        setUploadPreview(isEncounter ? { ...data, _encounter: true } : data);
        setExcludedRows(new Set());
      } else {
        setUploadError(data.error || "Failed to parse CSV");
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEncounterConfirm = async () => {
    if (!uploadFile) return;
    if (
      !window.confirm(
        `This will update ${formatNumber(uploadPreview.updates_planned)} existing claims and create ${formatNumber(uploadPreview.creates_planned)} new claims for this practice. Continue?`,
      )
    )
      return;

    setConfirming(true);
    setUploadError(null);

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("commit", "true");

    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/encounter-import`,
        { method: "POST", credentials: "include", body: formData },
      );
      const data = await response.json();
      if (response.ok) {
        setShowUploadModal(false);
        setUploadPreview(null);
        setUploadFile(null);
        setMessage({
          type: "success",
          text: `Encounter import complete: ${formatNumber(data.updates_written)} claims updated, ${formatNumber(data.creates_inserted)} created`,
        });
        setUploadedThisSession(true);
        loadClaims(1);
      } else {
        setUploadError(data.error || "Failed to import");
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setConfirming(false);
    }
  };

  const handleUploadConfirm = async () => {
    if (!uploadPreview?.claims?.length) return;

    // Filter out excluded rows for Olympus format
    const claimsToSubmit =
      uploadFormat === "olympus_submission"
        ? uploadPreview.claims.filter((_, i) => !excludedRows.has(i))
        : uploadPreview.claims;

    if (claimsToSubmit.length === 0) return;

    // No replace mode. An upload is always an upsert — it adds new claims and updates
    // the ones it matches, and never clears the ledger. The option that used to live
    // here sent `replace: true` to upload-confirm, which bulk-deleted every claim in
    // the practice; harmless on a practice holding money because the foreign keys
    // blocked it, and destructive on a newly onboarded one, which has no such rows.
    setConfirming(true);
    setUploadError(null);

    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/upload-confirm`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            claims: claimsToSubmit,
          }),
        },
      );
      const data = await response.json();
      if (response.ok) {
        setShowUploadModal(false);
        setUploadPreview(null);
        setUploadFile(null);
        setMessage({
          type: "success",
          text: `Created ${formatNumber(data.created)} claims`,
        });
        // If the import created stub payer rows (no payer_id), prompt the
        // user to fill them in on the Practice Details page.
        const needIds = data.roster?.payers_needing_id || 0;
        if (needIds > 0) {
          setPendingPayerNotice({ count: needIds, practiceId });
        }
        setUploadedThisSession(true);
        loadClaims(1);
      } else {
        setUploadError(data.error || "Failed to create claims");
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setConfirming(false);
    }
  };

  // --- Checkbox selection ---

  const showCheckboxes =
    selectedPractice?.clearinghouse === "stedi" && !readOnly;
  // Rules engine surfaces (backend services/rules_engine.py). 'advise' renders
  // preview chips only; 'enforce' also gates selection + submission.
  const rulesActive =
    selectedPractice?.clearinghouse === "stedi" &&
    selectedPractice?.rules_mode &&
    selectedPractice.rules_mode !== "off";
  const rulesEnforce = rulesActive && selectedPractice.rules_mode === "enforce";
  const submittableOnPage = claims.filter((c) =>
    SUBMITTABLE_STATUSES.includes(c.claim_status),
  );
  const allPageSubmittableSelected =
    submittableOnPage.length > 0 &&
    submittableOnPage.every((c) => selectedClaimIds.has(c.id));

  useEffect(() => {
    claims.forEach((c) => claimStatusByIdRef.current.set(c.id, c.claim_status));
  }, [claims]);

  const loadSubmissionStatus = useCallback(async () => {
    if (!practiceId) return;
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/submission-status`,
        { credentials: "include" },
      );
      if (!response.ok) {
        setSubmissionStatus(null);
        return;
      }
      const data = await response.json();
      setSubmissionStatus({ ...data, practiceId });
    } catch (err) {
      // A banner that cannot load stays hidden; the ledger itself is unaffected.
      setSubmissionStatus(null);
    }
  }, [practiceId]);

  useEffect(() => {
    setSubmissionStatus(null);
    loadSubmissionStatus();
  }, [loadSubmissionStatus]);

  const askResend = (count, total) =>
    new Promise((resolve) => setResendPrompt({ count, total, resolve }));
  const answerResend = (sendAnyway) => {
    const prompt = resendPrompt;
    setResendPrompt(null);
    if (prompt) prompt.resolve(sendAnyway);
  };

  const toggleClaimSelection = (claimId) => {
    setSelectedClaimIds((prev) => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId);
      else next.add(claimId);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    if (allPageSubmittableSelected) {
      setSelectedClaimIds((prev) => {
        const next = new Set(prev);
        submittableOnPage.forEach((c) => next.delete(c.id));
        return next;
      });
    } else {
      setSelectedClaimIds((prev) => {
        const next = new Set(prev);
        submittableOnPage.forEach((c) => next.add(c.id));
        return next;
      });
    }
  };

  const handleSelectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const allIds = new Set(selectedClaimIds);
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        // The list's own params builder, so "select all matching" selects exactly what the
        // filter bar shows. A hand-built copy here would silently drop any filter added
        // later, and this selection feeds Submit.
        const params = ledgerQueryParams(filters, sort);
        params.set("page", page);
        params.set("per_page", 200);

        const response = await fetch(
          `${API_URL}/api/practices/${practiceId}/claims?${params}`,
          { credentials: "include" },
        );
        if (!response.ok) break;
        const data = await response.json();
        (data.claims || []).forEach((c) => {
          claimStatusByIdRef.current.set(c.id, c.claim_status);
          if (SUBMITTABLE_STATUSES.includes(c.claim_status)) {
            allIds.add(c.id);
          }
        });
        hasMore = page < (data.pages || 1);
        page++;
      }
      setSelectedClaimIds(allIds);
    } catch (err) {
      // Silently fail
    } finally {
      setSelectingAll(false);
    }
  };

  // --- Submit to Stedi ---

  const handleSubmitToStedi = async () => {
    const count = selectedClaimIds.size;
    if (count === 0) return;

    // Enforce mode: flagged claims need an explicit, per-claim acknowledged
    // override (receipted server-side). Blocked claims can't be selected, but
    // re-check here in case of a stale page.
    let flaggedOverrides = [];
    if (rulesEnforce) {
      const selected = claims.filter((cl) => selectedClaimIds.has(cl.id));
      const blocked = selected.filter((cl) => cl.rules_verdict === "blocked");
      if (blocked.length > 0) {
        setMessage({
          type: "error",
          text: `${blocked.length} selected claim(s) are blocked by rules and were deselected.`,
        });
        setSelectedClaimIds((prev) => {
          const next = new Set(prev);
          blocked.forEach((cl) => next.delete(cl.id));
          return next;
        });
        return;
      }
      const flagged = selected.filter((cl) => cl.rules_verdict === "flagged");
      if (flagged.length > 0) {
        const lines = flagged
          .map((cl) => {
            const reasons = (cl.rules_verdict_detail || [])
              .map((d) => d.reason)
              .join("; ");
            return `- ${formatPatientName(cl.patient_name)} ${cl.date_of_service}: ${reasons || "flagged"}`;
          })
          .join("\n");
        if (
          !window.confirm(
            `${flagged.length} selected claim(s) are FLAGGED by rules:\n\n${lines}\n\nSubmit anyway? This override is recorded.`,
          )
        )
          return;
        flaggedOverrides = flagged.map((cl) => cl.id);
      }
    }

    // Already-sent claims in the selection: ask, defaulting to No. No sends only the
    // drafts and rejections; the server would skip the rest anyway, but the biller
    // should not learn that from a failure count.
    const selectedIds = [...selectedClaimIds];
    const alreadySent = selectedIds.filter(
      (id) =>
        !FIRST_SUBMISSION_STATUSES.includes(claimStatusByIdRef.current.get(id)),
    );
    let sendIds = selectedIds;
    let resubmit = false;
    if (alreadySent.length > 0) {
      resubmit = await askResend(alreadySent.length, selectedIds.length);
      if (!resubmit) {
        const skip = new Set(alreadySent);
        sendIds = selectedIds.filter((id) => !skip.has(id));
        if (sendIds.length === 0) {
          setMessage({
            type: "error",
            text: "Nothing sent: every selected claim was already submitted.",
          });
          return;
        }
      }
    }

    if (
      !window.confirm(
        `This will submit ${formatNumber(sendIds.length)} claim(s) to Stedi. Each submission costs money. Proceed?`,
      )
    )
      return;

    setSubmitting(true);
    setMessage(null);

    try {
      const body = { claim_ids: sendIds, flagged_overrides: flaggedOverrides };
      // Only ever the literal true, and only from "Send anyway", with the exact ids
      // the dialog counted: the server re-sends those and no others, so a claim this
      // page still thinks is a draft is never re-sent on the strength of this answer.
      if (resubmit) {
        body.resubmit = true;
        body.resubmit_claim_ids = alreadySent;
      }
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        },
      );
      const data = await response.json();
      if (response.ok) {
        let text = `Submitted: ${formatNumber(data.successful)} succeeded, ${formatNumber(data.failed)} failed out of ${formatNumber(data.total)}`;
        if (data.failed > 0 && data.details) {
          const errors = data.details.filter((d) => !d.success);
          const RULES_ERRORS = {
            blocked_by_rules:
              "Blocked by rules (see the Rules chip on the claim)",
            flagged_by_rules_needs_override:
              "Flagged by rules — confirm the warning to submit",
            already_submitted: "Already submitted — not sent again",
          };
          const uniqueErrors = [
            ...new Set(
              errors
                .map((d) => RULES_ERRORS[d.error] || d.error)
                .filter(Boolean),
            ),
          ];
          if (uniqueErrors.length > 0) {
            text +=
              "\n\nFailure reasons:\n" +
              uniqueErrors.map((e) => "- " + e).join("\n");
          }
        }
        setMessage({
          type: data.failed > 0 ? "error" : "success",
          text,
        });
        setSelectedClaimIds(new Set());
        loadClaims(claimsPage);
        loadSubmissionStatus();
      } else {
        setMessage({ type: "error", text: data.error || "Submission failed" });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Correct (replace / void) an adjudicated claim ---

  const ADJUDICATED = [
    "denied",
    "paid",
    "partial",
    "patient",
    "accepted",
    "appealed",
    "credit",
  ];

  const openCorrect = async (claim, e) => {
    e.stopPropagation();
    setCorrecting({
      claim,
      info: null,
      frequency: "7",
      pccn: "",
      reason: "",
      busy: true,
      error: null,
    });
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claim.id}/correction-info`,
        { credentials: "include" },
      );
      const info = await response.json();
      if (!response.ok)
        throw new Error(info.error || "Could not load correction details");
      setCorrecting(
        (c) =>
          c && {
            ...c,
            info,
            pccn: info.payer_claim_control_number || "",
            busy: false,
          },
      );
    } catch (err) {
      setCorrecting((c) => c && { ...c, busy: false, error: err.message });
    }
  };

  const submitCorrect = async (ev) => {
    ev.preventDefault();
    if (!correcting) return;
    const { claim, frequency, pccn, reason, info } = correcting;
    setCorrecting((c) => ({ ...c, busy: true, error: null }));
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claim.id}/correct`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            frequency,
            reason,
            payer_claim_control_number: info?.medicare ? null : pccn || null,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Could not create the corrected claim");
      setCorrecting(null);
      setMessage({
        type: "success",
        text:
          `Draft #${data.claim.id} created as a ${frequency === "8" ? "void" : "replacement"} of claim #${claim.id}. ` +
          (frequency === "8"
            ? "Submit it from the ledger."
            : "Edit its lines, then submit it from the ledger."),
      });
      await loadClaims(claimsPage);
      if (frequency === "7") handleEdit(data.claim);
    } catch (err) {
      setCorrecting((c) => c && { ...c, busy: false, error: err.message });
    }
  };

  // --- Retry single claim ---

  const handleRetry = async (claimId, e) => {
    e.stopPropagation();

    // Enforce mode: the same per-claim override the batch submit takes. A
    // blocked claim cannot be retried at all; a flagged one needs the warning
    // acknowledged, and the acknowledgment is receipted server-side.
    let overrideFlagged = false;
    if (rulesEnforce) {
      const cl = claims.find((c) => c.id === claimId);
      if (cl?.rules_verdict === "blocked") {
        setMessage({
          type: "error",
          text: "This claim is blocked by rules and cannot be retried until the rule is satisfied (see the Rules chip).",
        });
        return;
      }
      if (cl?.rules_verdict === "flagged") {
        const reasons = (cl.rules_verdict_detail || [])
          .map((d) => d.reason)
          .join("; ");
        if (
          !window.confirm(
            `This claim is FLAGGED by rules:\n\n${reasons || "flagged"}\n\nRetry anyway? This override is recorded.`,
          )
        )
          return;
        overrideFlagged = true;
      }
    }
    if (!window.confirm("Retry this claim submission? This costs money."))
      return;

    setRetryingClaimId(claimId);
    setMessage(null);

    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claimId}/retry`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ override_flagged: overrideFlagged }),
        },
      );
      const data = await response.json();
      if (response.ok && data.successful > 0) {
        setMessage({ type: "success", text: "Claim resubmitted successfully" });
      } else {
        const RULES_ERRORS = {
          blocked_by_rules:
            "Blocked by rules (see the Rules chip on the claim)",
          flagged_by_rules_needs_override:
            "Flagged by rules — confirm the warning to retry",
        };
        const raw = data.error || data.details?.[0]?.error || "Retry failed";
        setMessage({ type: "error", text: RULES_ERRORS[raw] || raw });
      }
      loadClaims(claimsPage);
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setRetryingClaimId(null);
    }
  };

  // --- On-demand claim status check (276/277) ---

  const handleCheckStatus = (claim, e) => {
    e.stopPropagation();
    // Soft guards: nudge (never block) when a re-check is likely pointless. F =
    // payer already finalized; U = payer unsupported (the check never reaches the
    // payer, so it is free). Everything else gets the normal cost prompt.
    let message;
    if ((claim.last_status_category || "").charAt(0) === "F") {
      message = `This claim already came back as ${claim.last_status_label || "finalized"} from the payer. Re-checking runs another live check and rarely changes. Check anyway?`;
    } else if (claim.last_status_category === "U") {
      message = `${claim.payer_name || "This payer"} does not support automated status checks, so a check will not reach the payer. This rarely changes. Check anyway?`;
    } else {
      message = "This runs one live status check with the payer.";
    }
    setStatusModal({
      title: "Check claim status?",
      message,
      confirmLabel: "Check now",
      onConfirm: () => runStatusCheck(claim),
    });
  };

  // Update a row's cached payer-status in local state so the column reflects a
  // check immediately (no reload). Mirrors the backend cache rules: error caches
  // nothing; unsupported caches the "U" sentinel; success/no_match cache the
  // real category + label.
  const patchClaimCache = (claimId, chk) => {
    if (!chk || chk.outcome === "error") return;
    const isUnsupported = chk.outcome === "unsupported";
    setClaims((prev) =>
      prev.map((c) =>
        c.id === claimId
          ? {
              ...c,
              last_status_source: chk.source || c.last_status_source,
              last_status_category: isUnsupported
                ? "U"
                : chk.status_category_code,
              last_status_label: isUnsupported
                ? "Unsupported"
                : chk.mapped_status,
              last_status_checked_at:
                chk.checked_at || new Date().toISOString(),
            }
          : c,
      ),
    );
  };

  const runStatusCheck = async (claim) => {
    setCheckingStatusId(claim.id);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claim.id}/status-check`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      const data = await response.json();
      if (response.ok) {
        const chk = data.check || {};
        const label = chk.mapped_status || "an unknown status";
        if (chk.outcome === "unsupported") {
          setStatusModal({
            title: "Status check not available",
            message: `${claim.payer_name || "This payer"} does not support automated status checks, so we could not check this claim through Stedi. No charge.`,
          });
        } else if (data.reused) {
          setStatusModal({
            title: "Checked recently",
            message: `This claim was just checked, so we are showing the most recent result with no new charge. The payer reports it as ${label}.`,
          });
        } else if (chk.outcome === "error") {
          setStatusModal({
            title: "Status check failed",
            message: `We could not get a status from the payer${chk.error_detail ? ` (${chk.error_detail})` : ""}. No charge. You can try again.`,
          });
        } else {
          const money = (v) =>
            Number(v).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          const bits = [];
          if (chk.amount_paid != null) {
            let paid = `paid $${money(chk.amount_paid)}`;
            if (chk.submitted_amount != null)
              paid += ` out of $${money(chk.submitted_amount)}`;
            bits.push(paid);
          }
          if (chk.payment_date) bits.push(`effective ${chk.payment_date}`);
          const paidStr = bits.length ? ` It reports ${bits.join(", ")}.` : "";
          setStatusModal({
            title: "Status updated",
            message: `The payer reports this claim as ${label}.${paidStr} It has been added to the claim history below.`,
          });
        }
        // Patch this row's cached payer-status in place so the column updates
        // immediately, without a full-page reload.
        patchClaimCache(claim.id, chk);
        // Refresh only this claim's timeline — not the whole list.
        loadClaimTimeline(claim.id);
      } else {
        setStatusModal({
          title: "Status check failed",
          message: data.error || "Something went wrong. Please try again.",
        });
      }
    } catch (err) {
      setStatusModal({ title: "Status check failed", message: err.message });
    } finally {
      setCheckingStatusId(null);
    }
  };

  const loadClaimTimeline = async (claimId) => {
    setLoadingTimeline(true);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claimId}/history`,
        {
          credentials: "include",
        },
      );
      const data = response.ok ? await response.json() : { timeline: [] };
      setClaimTimeline((prev) => ({ ...prev, [claimId]: data.timeline || [] }));
    } catch (err) {
      setClaimTimeline((prev) => ({ ...prev, [claimId]: [] }));
    } finally {
      setLoadingTimeline(false);
    }
  };

  // 401 and 403 come back as Flask's HTML pages, not JSON, so calling response.json() on them
  // throws and the raw parser text ("Unexpected token '<'...") ends up in front of the user.
  const readFetchError = async (response, fallback) => {
    if (response.status === 401)
      return "Your session has expired. Sign in again.";
    if (response.status === 403)
      return "You do not have permission to do that.";
    try {
      const data = await response.json();
      return data.error || fallback;
    } catch (err) {
      return fallback;
    }
  };

  const loadClaimComments = async (claimId) => {
    const seq = (commentSeq.current[claimId] || 0) + 1;
    commentSeq.current[claimId] = seq;
    const superseded = () => commentSeq.current[claimId] !== seq;

    setCommentLoadError((prev) => ({ ...prev, [claimId]: null }));
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claimId}/comments`,
        {
          credentials: "include",
        },
      );
      if (superseded()) return;
      if (!response.ok) {
        const message = await readFetchError(
          response,
          "Could not load comments.",
        );
        if (superseded()) return;
        setCommentLoadError((prev) => ({ ...prev, [claimId]: message }));
        return;
      }
      const data = await response.json();
      if (superseded()) return;
      const list = data.comments || [];
      setClaimComments((prev) => ({ ...prev, [claimId]: list }));
      return list;
    } catch (err) {
      if (superseded()) return;
      setCommentLoadError((prev) => ({
        ...prev,
        [claimId]:
          "Could not load comments. Check your connection and try again.",
      }));
    }
  };

  const handleAddComment = async (claimId) => {
    const body = (commentDrafts[claimId] || "").trim();
    if (!body) return;
    setPostingCommentFor(claimId);
    setCommentError((prev) => ({ ...prev, [claimId]: null }));
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claimId}/comments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ body }),
        },
      );
      // response.ok BEFORE response.json(), because a 401 or 403 is an HTML page.
      if (!response.ok) {
        const message = await readFetchError(
          response,
          "Could not add the comment.",
        );
        setCommentError((prev) => ({ ...prev, [claimId]: message }));
        return;
      }
      setCommentDrafts((prev) => ({ ...prev, [claimId]: "" }));
      // Refetch rather than append. Appending assumed the list only ever grows for US, but
      // other people add comments and resolve them, so the pane could be missing someone
      // else's remark while its author composed against a thread they could not fully see.
      // The refetch also bumps the sequence, discarding any list GET still in flight from
      // before this comment existed.
      const list = await loadClaimComments(claimId);
      // The row's icon and tooltip come from the claims list, which is fetched once when the
      // page loads. Without this the row keeps advertising the PREVIOUS comment until
      // something else reloads it, which reads as the tooltip showing the wrong one.
      // Derived from the refetch rather than from our own POST, so a remark someone else
      // added a second earlier is what the row shows -- it is genuinely the most recent.
      if (Array.isArray(list)) {
        const newest = list[list.length - 1] || null;
        setClaims((prev) =>
          prev.map((row) =>
            row.id === claimId
              ? {
                  ...row,
                  comment_count: list.length,
                  last_comment: newest && {
                    author_name: newest.author_name,
                    body: commentPreview(newest.body),
                    created_at: newest.created_at,
                  },
                }
              : row,
          ),
        );
      }
    } catch (err) {
      setCommentError((prev) => ({
        ...prev,
        [claimId]:
          "Could not add the comment. Check your connection and try again.",
      }));
    } finally {
      setPostingCommentFor(null);
    }
  };

  const handleRowExpand = async (claimId, claimLinesCount) => {
    if (expandedClaimId === claimId) {
      setExpandedClaimId(null);
      setExpandedClaimLines(null);
      setExpandedClaimFooting(null);
      return;
    }
    setExpandedClaimId(claimId);
    setExpandedClaimLines(null);
    setExpandedClaimFooting(null);
    loadClaimTimeline(claimId);
    loadClaimComments(claimId);
    if (claimLinesCount > 0) {
      setLoadingClaimLines(true);
      try {
        const response = await fetch(
          `${API_URL}/api/practices/${practiceId}/claims/${claimId}/lines`,
          {
            credentials: "include",
          },
        );
        if (response.ok) {
          const data = await response.json();
          setExpandedClaimLines(data.lines || []);
          setExpandedClaimFooting(
            data.total ? { total: data.total, unallocated: data.unallocated || null } : null,
          );
        } else {
          setExpandedClaimLines([]);
        }
      } catch (err) {
        setExpandedClaimLines([]);
      } finally {
        setLoadingClaimLines(false);
      }
    }
  };

  if (!selectedPractice) return null;

  // `payer` is an array, so it needs .length rather than truthiness — an empty array
  // is truthy and would leave Clear on screen with nothing to clear.
  const anyFilterSet = !!(
    filters.status ||
    filters.date_from ||
    filters.date_to ||
    filters.provider_ids.length ||
    filters.payer.length ||
    filters.patient ||
    filters.patient_id ||
    filters.balance ||
    filters.created ||
    filters.updated ||
    filters.flagged
  );

  const columns = [
    // The payer's claim control number, sorted on the column it is stored in. First
    // column because it is the number a biller reads off a payer's remittance or
    // repeats down the phone, so it is what they scan the ledger for.
    { key: "claim_control_number", label: "ID" },
    { key: "date_of_service", label: "Date" },
    { key: "patient_name", label: "Patient" },
    { key: null, label: "Provider" },
    { key: "payer_name", label: "Payer" },
    { key: "billed_amount", label: "Billed" },
    { key: null, label: "Adj" },
    { key: "insurance_paid", label: "Paid" },
    { key: null, label: "Balance" },
    ...(rulesActive
      ? [
          {
            key: "rules_verdict",
            label:
              selectedPractice.rules_mode === "advise"
                ? "Rules preview"
                : "Rules",
          },
        ]
      : []),
    { key: "claim_status", label: "Status" },
  ];

  return (
    <div className="claims-ledger-page">
      {/* The practice's claim lists. Not on a list's own page: its top bar is the list. */}
      {!listMode && (
        <ClaimListsStrip
          practiceId={practiceId}
          refreshKey={listsVersion}
          onOpenList={onOpenList}
        />
      )}
      {!listMode && (
      <div className="ledger-header">
        <h1 className="page-title">Claims Ledger</h1>
        {!readOnly && (
          <div className="ledger-header-actions">
            {selectedPractice?.clearinghouse === "stedi" && (
              <button
                className="btn btn-primary"
                onClick={handleSubmitToStedi}
                disabled={submitting || selectedClaimIds.size === 0}
              >
                {submitting
                  ? "Submitting..."
                  : `Submit Selected (${formatNumber(selectedClaimIds.size)})`}
              </button>
            )}
            {rulesActive && (
              <button
                className="btn btn-secondary"
                title="Select every draft or rejected claim on this page with a clean rules verdict"
                onClick={() => {
                  setSelectedClaimIds((prev) => {
                    const next = new Set(prev);
                    claims.forEach((cl) => {
                      if (
                        FIRST_SUBMISSION_STATUSES.includes(cl.claim_status) &&
                        cl.rules_verdict === "clean"
                      )
                        next.add(cl.id);
                    });
                    return next;
                  });
                }}
              >
                Select all clean
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowUploadModal(true);
                setUploadFile(null);
                setAsyncFile(null);
                setUploadPreview(null);
                setUploadError(null);
                setUploadFormat("");
                setExcludedRows(new Set());
                setStaleConfirm(null);
              }}
            >
              Upload CSV
            </button>
            {features.exports && (
              <ExportButton
                practiceId={practiceId}
                kind="ledger"
                getParams={() => ledgerQueryParams(filters, sort)}
                readOnly={readOnly}
              />
            )}
            {importStatus?.last_imported_at && (
              <div className="ledger-last-refreshed">
                Last refreshed at{" "}
                {new Date(importStatus.last_imported_at).toLocaleString(
                  undefined,
                  {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  },
                )}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Not dismissable: it goes away when a submission goes out. A practice Atlas
          submits for that has sent nothing in a week must be impossible to miss. */}
      {submissionStatus?.level === "alarm" &&
        submissionStatus.practiceId === practiceId && (
          <div className="ledger-submission-alarm" role="alert">
            {submissionStatus.line}
          </div>
        )}

      {/* Dismissable message */}
      {message && (
        <div className={`ledger-message ${message.type}`}>
          <span style={{ whiteSpace: "pre-line" }}>{message.text}</span>
          <button className="message-close" onClick={() => setMessage(null)}>
            &times;
          </button>
        </div>
      )}

      {/* Import status chip (async /import-file). Amber running / Green done / red-ish stalled;
          the Red error state waits on the last_import_error column. Green is transient — shown
          only for a completion observed this session, never for a stale past import. */}
      {(() => {
        if (listMode) return null;
        const s = importStatus?.status;
        const regressions = importStatus?.regressions || 0;
        const chipMode =
          s === "running"
            ? "running"
            : s === "error"
              ? "error"
              : s === "stalled"
                ? "stalled"
                : s === "success" && importDoneThisSession && regressions > 0
                  ? "review"
                  : s === "success" && importDoneThisSession
                    ? "success"
                    : null;
        if (!chipMode) return null;
        const isReview = chipMode === "review";
        return (
          <div
            className={`import-chip import-chip-${chipMode}${isReview ? " import-chip-clickable" : ""}`}
            {...(isReview
              ? {
                  role: "button",
                  tabIndex: 0,
                  onClick: () =>
                    setFilters({ ...defaultFilters(), flagged: "1" }),
                  onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setFilters({ ...defaultFilters(), flagged: "1" });
                    }
                  },
                }
              : {})}
          >
            {chipMode === "running" && (
              <>
                <span className="import-chip-spinner" />
                <span>
                  Import in progress. This can take a few minutes. You can leave
                  this page.
                </span>
              </>
            )}
            {chipMode === "success" && (
              <span>
                Import complete
                {importStatus.last_imported_at
                  ? `. Last updated ${formatTimestamp(importStatus.last_imported_at)}`
                  : ""}
                .
              </span>
            )}
            {chipMode === "review" && (
              <span>
                Import complete, but {formatNumber(regressions)} claim
                {regressions === 1 ? "" : "s"} had lower collections than
                before. Review before relying on these numbers, in case an older
                file was uploaded.
              </span>
            )}
            {chipMode === "error" && (
              <span>
                Import failed.{" "}
                {importStatus.error || "Check the file and try again."}
              </span>
            )}
            {chipMode === "stalled" && (
              <span>
                The last import didn’t finish. Try uploading the file again.
              </span>
            )}
          </div>
        );
      })()}

      {/* Filter Bar */}
      <div className="ledger-filter-bar">
        <StatusFilter
          value={filters.status}
          onChange={(status) => setFilters({ ...filters, status })}
          clientView={readOnly}
        />
        <input
          type="date"
          min={DATE_INPUT_MIN}
          max={DATE_INPUT_MAX}
          value={filters.date_from}
          onChange={(e) =>
            setFilters({ ...filters, date_from: e.target.value })
          }
        />
        <input
          type="date"
          min={DATE_INPUT_MIN}
          max={DATE_INPUT_MAX}
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
        />
        {providers.length > 0 && (
          <ProviderFilter
            value={filters.provider_ids}
            providers={providers}
            onChange={(provider_ids) => setFilters({ ...filters, provider_ids })}
          />
        )}
        <input
          type="text"
          value={filters.patient}
          onChange={(e) =>
            setFilters({ ...filters, patient: e.target.value, patient_id: "" })
          }
          placeholder="Search patient..."
          className="ledger-payer-search"
        />
        <PayerFilter
          value={filters.payer}
          options={payerOptions}
          onChange={(payer) => setFilters({ ...filters, payer })}
        />
        {/* An A/R click narrows to claims still carrying a balance, which no other
            filter can say, so it shows as its own pill with a × to drop it. */}
        {filters.balance && (
          <button
            type="button"
            className="ledger-import-pill active ledger-balance-pill"
            onClick={() => setFilters({ ...filters, balance: "" })}
            title="Only claims that still carry a balance, as the A/R page counts them"
          >
            {filters.balance === "patient" ? "Patient balance due" : "Insurance balance due"} ×
          </button>
        )}
        {anyFilterSet && (
          <button
            className="btn-link"
            onClick={() => setFilters(defaultFilters())}
          >
            Clear
          </button>
        )}
        {/* The import pills sit AFTER Clear so it keeps its place beside the payer
            dropdown: the pills come and go with an upload, and a Clear button that
            slid along the row each time one appeared would be harder to aim at.

            Gated on uploadedThisSession as well as the counts. The counts are
            practice-level and persist on the Practice row, so without the gate every
            user of the practice sees the last uploader's New/Updated/Flagged totals,
            forever, as though they had just imported something themselves. */}
        {uploadedThisSession && importStatus?.created > 0 && (
          <button
            type="button"
            className={`ledger-import-pill ledger-import-pill-new${filters.created ? " active" : ""}`}
            onClick={() => toggleImportFilter("created")}
          >
            New: {formatNumber(importStatus.created)}
          </button>
        )}
        {uploadedThisSession &&
          importStatus?.updated - importStatus?.regressions > 0 && (
            <button
              type="button"
              className={`ledger-import-pill ledger-import-pill-updated${filters.updated ? " active" : ""}`}
              onClick={() => toggleImportFilter("updated")}
            >
              Updated:{" "}
              {formatNumber(importStatus.updated - importStatus.regressions)}
            </button>
          )}
        {uploadedThisSession && importStatus?.regressions > 0 && (
          <button
            type="button"
            className={`ledger-import-pill ledger-import-pill-flagged${filters.flagged ? " active" : ""}`}
            onClick={() => toggleImportFilter("flagged")}
          >
            Flagged: {formatNumber(importStatus.regressions)}
          </button>
        )}
      </div>

      {/* Select all matching banner */}
      {showCheckboxes && allPageSubmittableSelected && claimsPages > 1 && (
        <div className="select-all-banner">
          All {formatNumber(submittableOnPage.length)} submittable claims on
          this page are selected.{" "}
          <button
            className="btn-link"
            onClick={handleSelectAllMatching}
            disabled={selectingAll}
          >
            {selectingAll
              ? "Loading..."
              : "Select all matching current filters"}
          </button>
        </div>
      )}

      {/* Claim lists: tick rows, then make a list of them or add them to an open one. */}
      <ClaimListActions
        practiceId={practiceId}
        selectedIds={[...listSelectedIds]}
        source="manual"
        onDone={onListWritten}
        onOpenList={onOpenList}
      />

      {/* Claims Table.

          The spinner replaces the table only when there is nothing to show. With rows
          already on screen — restored from the cache on a remount, or the previous
          result during a refilter — they stay put and dim instead, so returning to the
          page does not blank it and a filter change does not make the table jump. */}
      {loading && claims.length === 0 ? (
        <div className="ledger-loading">
          <div className="spinner"></div>
          <span>Loading claims...</span>
        </div>
      ) : claims.length === 0 ? (
        <div className="ledger-empty">No claims found.</div>
      ) : (
        <>
          <div
            className={`ledger-table-wrapper${loading ? " is-refreshing" : ""}`}
          >
            <table className="ledger-table">
              <thead>
                <tr>
                  <th className="ledger-list-col">
                    <input
                      type="checkbox"
                      aria-label="Select every claim on this page for a list"
                      title="Select every claim on this page for a list"
                      checked={allPageListSelected}
                      onChange={toggleAllListOnPage}
                    />
                  </th>
                  {showCheckboxes && (
                    <th className="ledger-checkbox-col">
                      <input
                        type="checkbox"
                        checked={allPageSubmittableSelected}
                        onChange={toggleAllOnPage}
                        disabled={submittableOnPage.length === 0}
                        title="Select all submittable claims on this page"
                      />
                    </th>
                  )}
                  {columns.map((col, i) => (
                    <th
                      key={i}
                      className={col.key ? "sortable" : ""}
                      onClick={() => col.key && handleSort(col.key)}
                    >
                      {col.label}
                      {col.key && sort.field === col.key && (
                        <span className="sort-arrow">
                          {sort.order === "asc" ? " \u25B2" : " \u25BC"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {claims.map((c) => (
                  <React.Fragment key={c.id}>
                    <tr
                      className={`ledger-row row-status-${c.claim_status || "draft"} ${expandedClaimId === c.id ? "expanded" : ""}`}
                      onClick={() => handleRowExpand(c.id, c.claim_lines_count)}
                    >
                      <td
                        className="ledger-list-col"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          aria-label="Select for a list"
                          title="Select for a list"
                          checked={listSelectedIds.has(c.id)}
                          onChange={() => toggleListSelection(c.id)}
                        />
                      </td>
                      {showCheckboxes && (
                        <td
                          className="ledger-checkbox-col"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {SUBMITTABLE_STATUSES.includes(c.claim_status) &&
                            (rulesEnforce && c.rules_verdict === "blocked" ? (
                              <input
                                type="checkbox"
                                disabled
                                checked={false}
                                readOnly
                                title="Blocked by rules — open the Rules tab for reasons"
                              />
                            ) : (
                              <input
                                type="checkbox"
                                checked={selectedClaimIds.has(c.id)}
                                onChange={() => toggleClaimSelection(c.id)}
                              />
                            ))}
                        </td>
                      )}
                      {/* title, because the column ellipsises at ~20 characters and the
                          CLN no longer appears anywhere else — it was removed from the
                          Tracking panel when it moved here, so a long control number
                          would otherwise be unreadable in the whole UI. */}
                      <td
                        className="col-claim-id"
                        title={c.claim_control_number || undefined}
                      >
                        {c.claim_control_number || "-"}
                      </td>
                      <td>{c.date_of_service}</td>
                      <td className="col-name">
                        {formatPatientName(c.patient_name)}
                        {SHOW_CLAIM_COMMENTS && c.comment_count > 0 && (
                          <span
                            className="row-comment-flag"
                            title={commentTooltip(
                              c.last_comment,
                              c.comment_count,
                            )}
                            aria-label={`${c.comment_count} comment${c.comment_count === 1 ? "" : "s"} on this claim`}
                          >
                            <CommentFlagIcon />
                          </span>
                        )}
                      </td>
                      <td>{c.rendering_provider_name || "-"}</td>
                      <td>{c.payer_name || "-"}</td>
                      <td className="col-currency">
                        {formatCurrency(c.billed_amount)}
                      </td>
                      <td className="col-currency">
                        {formatCurrency(c.adjustment_amount)}
                      </td>
                      <td className="col-currency">
                        {formatCurrency(
                          (c.insurance_paid || 0) + (c.patient_payment || 0),
                        )}
                      </td>
                      <td className="col-currency">
                        {formatCurrency(c.remaining_balance)}
                      </td>
                      {rulesActive && (
                        <td
                          className="col-rules"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <RulesChip
                            claim={c}
                            onOpen={() => {
                              handleRowExpand(c.id, c.claim_lines_count);
                              setDetailTab((prev) => ({
                                ...prev,
                                [c.id]: "rules",
                              }));
                            }}
                          />
                        </td>
                      )}
                      <td>
                        <span
                          className="ledger-status-badge"
                          style={{ background: statusColor(claimStatusKey(c, readOnly)) }}
                        >
                          {claimStatusText(c, readOnly)}
                        </span>
                        {/* Follow-up state on the claims a worklist exists for. On any
                            other status it would be noise — nothing is being chased. */}
                        {["rejected", "denied"].includes(c.claim_status) &&
                          followupMap[c.id] && (
                            <FollowupChip
                              followup={followupMap[c.id].followup}
                              overdue={followupMap[c.id].overdue}
                            />
                          )}
                      </td>
                    </tr>
                    {expandedClaimId === c.id && (
                      <tr className="ledger-detail-row">
                        <td colSpan={columns.length + 1 + (showCheckboxes ? 1 : 0)}>
                          <div className="ledger-detail-grid">
                            {/* Left column: what the claim IS — charges, then how it was tracked. */}
                            <div className="detail-left">
                              <div className="charge-detail-section">
                                <h4>Charges</h4>
                                {(c.icd_codes || []).length > 0 && (
                                  <div className="charge-icd-line">
                                    <span className="charge-icd-label">
                                      ICD-10:
                                    </span>
                                    <span>{formatIcd10List(c.icd_codes)}</span>
                                  </div>
                                )}
                                {loadingClaimLines &&
                                expandedClaimId === c.id ? (
                                  <div
                                    style={{
                                      padding: "8px 0",
                                      color: "var(--text-muted)",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Loading lines...
                                  </div>
                                ) : expandedClaimLines &&
                                  expandedClaimId === c.id ? (
                                  <table className="charge-detail-table">
                                    <thead>
                                      <tr>
                                        <th>CPT</th>
                                        <th>Modifiers</th>
                                        <th>Units</th>
                                        <th>Charge</th>
                                        <th>Adj</th>
                                        <th>Allowed</th>
                                        <th>Patient</th>
                                        <th>Paid</th>
                                        <th>Balance</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedClaimLines.map((line, i) => {
                                        // Footed figures (claim_line_footing): the claim's own
                                        // money placed on the lines so the table adds up to the
                                        // claim row. Present on every line the route serves;
                                        // the pre-footing fields stay as the fallback.
                                        const footed = line.foot_paid != null;
                                        const cptLabel =
                                          line.cpt_code === HEADER_ONLY_CPT
                                            ? "—"
                                            : line.cpt_code;
                                        const modifierLabel = (
                                          line.modifiers || []
                                        )
                                          .filter(Boolean)
                                          .join(", ");
                                        const totalAdj =
                                          (Number(
                                            line.contractual_adjustment,
                                          ) || 0) +
                                          (Number(line.other_adjustment) || 0);
                                        const adjCodes = line.codes || [
                                          ...new Set(
                                            (line.adjudications || [])
                                              .filter(
                                                (a) =>
                                                  a.type === "adjustment" &&
                                                  a.group_code &&
                                                  a.reason_code,
                                              )
                                              .map(
                                                (a) =>
                                                  `${a.group_code}-${a.reason_code}`,
                                              ),
                                          ),
                                        ];
                                        // What the vendor export reported for this line. Only present when no
                                        // 835 has adjudicated it (see claim_line_service.imported_money_by_line),
                                        // so the two can never be shown blended — an adjudicated line always
                                        // renders the adjudicated figures below.
                                        const impPaid = line.imported_paid;
                                        const impAdj = line.imported_adjustment;
                                        const impAllowed =
                                          line.imported_allowed;
                                        const impPatient =
                                          line.imported_patient_resp;
                                        return (
                                          <tr key={i}>
                                            <td>{cptLabel}</td>
                                            <td>{modifierLabel || "-"}</td>
                                            <td>{line.units ?? "-"}</td>
                                            <td>
                                              {formatCurrency(
                                                line.charge_amount,
                                              )}
                                            </td>
                                            <td>
                                              {footed
                                                ? formatCurrency(
                                                    line.foot_adjustment || null,
                                                  )
                                                : impAdj != null
                                                  ? formatCurrency(impAdj)
                                                  : formatCurrency(
                                                      totalAdj || null,
                                                    )}
                                              {adjCodes.length > 0 && (
                                                <div className="cell-sub">
                                                  {adjCodes.map((code, n) => (
                                                    <span
                                                      key={code}
                                                      title={CARC_DESCRIPTIONS[code]}
                                                    >
                                                      {n > 0 ? ", " : ""}
                                                      {code}
                                                    </span>
                                                  ))}
                                                </div>
                                              )}
                                            </td>
                                            <td>
                                              {impAllowed != null
                                                ? formatCurrency(impAllowed)
                                                : formatCurrency(
                                                    line.allowed_amount,
                                                  )}
                                            </td>
                                            <td>
                                              {impPatient != null
                                                ? formatCurrency(impPatient)
                                                : formatCurrency(
                                                    line.patient_responsibility,
                                                  )}
                                            </td>
                                            <td>
                                              {/* Amount only. The footing still splits paid into
                                                  insurance / patient / claim-level allocation
                                                  (foot_insurance_paid, foot_patient_paid,
                                                  foot_allocated); David asked 2026-10-23 that the
                                                  split not be printed under PAID. */}
                                              {footed
                                                ? formatCurrency(
                                                    line.foot_paid || null,
                                                  )
                                                : impPaid != null
                                                  ? formatCurrency(impPaid)
                                                  : formatCurrency(
                                                      line.insurance_paid,
                                                    )}
                                            </td>
                                            <td>
                                              {footed
                                                ? formatCurrency(
                                                    line.foot_balance,
                                                  )
                                                : "-"}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      {expandedClaimFooting?.unallocated && (
                                        <tr className="charge-detail-unallocated">
                                          <td colSpan={3}>
                                            Claim-level (unallocated)
                                          </td>
                                          <td>
                                            {formatCurrency(
                                              expandedClaimFooting.unallocated
                                                .charge || null,
                                            )}
                                          </td>
                                          <td>
                                            {formatCurrency(
                                              expandedClaimFooting.unallocated
                                                .adjustment || null,
                                            )}
                                          </td>
                                          <td>-</td>
                                          <td>-</td>
                                          <td>
                                            {formatCurrency(
                                              expandedClaimFooting.unallocated
                                                .paid || null,
                                            )}
                                          </td>
                                          <td>
                                            {formatCurrency(
                                              expandedClaimFooting.unallocated
                                                .balance,
                                            )}
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                    {expandedClaimFooting?.total && (
                                      <tfoot>
                                        <tr className="charge-detail-total">
                                          <td colSpan={3}>Total</td>
                                          <td>
                                            {formatCurrency(
                                              expandedClaimFooting.total.charge,
                                            )}
                                          </td>
                                          <td>
                                            {formatCurrency(
                                              expandedClaimFooting.total
                                                .adjustment || null,
                                            )}
                                          </td>
                                          <td>-</td>
                                          <td>-</td>
                                          <td>
                                            {formatCurrency(
                                              expandedClaimFooting.total.paid,
                                            )}
                                          </td>
                                          <td>
                                            {formatCurrency(
                                              expandedClaimFooting.total.balance,
                                            )}
                                          </td>
                                        </tr>
                                      </tfoot>
                                    )}
                                  </table>
                                ) : (
                                  <>
                                    {/* These rows are the claim header's CPT codes
                                    (practice_claims.cpt_codes), not line records (staging walk
                                    2026-09-13, item 2). Deliberately silent on whether the claim
                                    HAS charge lines: this table renders when there are no legacy
                                    line rows, and a coded claim can still carry ledger lines on its
                                    encounter. Whether a posting can land is said by the postings
                                    panel (`postable_reason`), from the same lookup the write-off uses. */}
                                    <table className="charge-detail-table charge-detail-table-header-only">
                                      <thead>
                                        <tr>
                                          <th>CPT</th>
                                          <th>Modifiers</th>
                                          <th>Units</th>
                                          <th>Charge</th>
                                          <th>Adj</th>
                                          <th>Allowed</th>
                                          <th>Patient</th>
                                          <th>Paid</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(c.cpt_codes || []).map((cp, i) => {
                                          const modifierLabel = (
                                            cp.modifiers || []
                                          )
                                            .filter(Boolean)
                                            .join(", ");
                                          // An IMPORTED line keeps its money under `adjudication`
                                          // (claim_import_parser._build_cpt_line) rather than flat on
                                          // the entry — a bare `paid` beside `charge` would read as
                                          // though the charge itself had been paid. Without reading
                                          // it here the four money cells render empty, which is what
                                          // makes a home-health balance impossible to follow: the
                                          // allowed amount the balance is measured from is exactly
                                          // the number that goes missing. Flat keys stay as the
                                          // fallback for the older converters that write them.
                                          const adj = cp.adjudication || {};
                                          const lineAdj =
                                            adj.contractual ?? cp.adjustment;
                                          const lineAllowed =
                                            adj.allowed ?? cp.allowed;
                                          const linePatient =
                                            adj.patient_responsibility ??
                                            cp.patient_resp;
                                          const linePaid =
                                            adj.insurance_paid ?? cp.paid;
                                          const lineCodes =
                                            adj.carc || cp.adj_codes;
                                          return (
                                            <tr key={i}>
                                              <td>{cp.code}</td>
                                              <td>{modifierLabel || "-"}</td>
                                              <td>{cp.units ?? "-"}</td>
                                              <td>
                                                {formatCurrency(cp.charge)}
                                              </td>
                                              <td>
                                                {formatCurrency(lineAdj)}
                                                {lineCodes?.length > 0 && (
                                                  <div className="cell-sub">
                                                    {lineCodes.join(", ")}
                                                  </div>
                                                )}
                                              </td>
                                              <td>
                                                {formatCurrency(lineAllowed)}
                                              </td>
                                              <td>
                                                {formatCurrency(linePatient)}
                                              </td>
                                              <td>
                                                {formatCurrency(linePaid)}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </>
                                )}
                              </div>

                              {/* Rejection callout. Shows only the denial-group codes the
                                server split out (denial_codes), with the clearinghouse text
                                as the free-text reason: the component still falls back to
                                `notes` on purpose, because claimmd_service writes
                                clearinghouse correspondence there and on a rejected claim
                                with no submission history that text IS the rejection
                                reason. Nothing human writes the column any more — see the
                                update route. */}
                              {!(loadingClaimLines && expandedClaimId === c.id) && (
                                <RejectionReason
                                  claim={c}
                                  descriptions={CARC_DESCRIPTIONS}
                                  lineCodes={
                                    expandedClaimId === c.id
                                      ? (expandedClaimLines || []).flatMap(
                                          (l) => l.codes || [],
                                        )
                                      : []
                                  }
                                />
                              )}

                              {/* Write-offs and manual payments. Its own component and its
                                own fetch: this file is shared with another strand and the
                                agreed change here is a mount, not a feature. */}
                              {/* `isAdmin`, not just the feature. `writeoffs` needs no
                                capability so it is on for every practice, while every
                                endpoint behind this panel is @role_required("admin").
                                Without this a biller expanding any claim fired the GET,
                                got 403, and read "Insufficient permissions" in a red
                                block on every claim forever — next to two buttons that
                                would also 403. */}
                              {features.writeoffs && isAdmin && (
                                <ClaimPostings
                                  practiceId={selectedPractice?.id}
                                  claim={c}
                                  readOnly={readOnly}
                                  onMoneyChanged={() => loadClaims(claimsPage)}
                                />
                              )}

                              {/* Pinned to the foot of the column by margin-top: auto, so the
                                tracking block sits on the same line as the buttons and stays
                                level with the bottom of the panel opposite instead of floating
                                directly under a charge table whose height varies per claim. */}
                              <div className="tracking-row">
                                {/* Tracking, under the charges rather than beside them. */}
                                <div className="tracking-section">
                                  <h4>Tracking Information</h4>
                                  {/* CLN moved to the ledger's leftmost ID column, so repeating
                                    it here would be the same number twice on one screen.
                                    Clearinghouse and Tracking ID were dropped as not useful to
                                    a biller reading this panel. Nothing about the underlying
                                    fields changed — all three are still on the claim and still
                                    returned by the API. */}
                                  {/* One column, not two. Three fields split across a 2-up grid
                                    left a ragged block with a gap where the fourth used to be;
                                    stacked, they read as one list. */}
                                  <div className="tracking-columns tracking-columns-single">
                                    <div>
                                      <div className="tracking-field">
                                        <span className="tracking-label">
                                          Member ID
                                        </span>{" "}
                                        {c.member_id || "-"}
                                      </div>
                                      <div className="tracking-field">
                                        <span className="tracking-label">
                                          Submitted
                                        </span>{" "}
                                        {c.date_submitted || "-"}
                                      </div>
                                      <div className="tracking-field">
                                        <span className="tracking-label">
                                          Paid
                                        </span>{" "}
                                        {c.date_paid || "-"}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {!readOnly && (
                                  <div className="detail-actions">
                                    {[
                                      "submitted",
                                      "accepted",
                                      "appealed",
                                    ].includes(c.claim_status) && (
                                      <button
                                        className="btn btn-primary btn-sm"
                                        onClick={(e) => handleCheckStatus(c, e)}
                                        disabled={checkingStatusId === c.id}
                                      >
                                        {checkingStatusId === c.id
                                          ? "Checking..."
                                          : "Check Status"}
                                      </button>
                                    )}
                                    <button
                                      className="btn btn-secondary btn-sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(c);
                                      }}
                                    >
                                      Edit
                                    </button>
                                    {/* Instance F's claim editor. Hidden rather than left
                                      to 409 on an adjudicated claim: F's apply_edit
                                      refuses those and routes them to the corrected-claim
                                      flow, and offering a row whose only answer is an
                                      error is a worse explanation than not offering it.
                                      `rejected` stays editable on purpose — the payer
                                      never saw it. */}
                                    {features.claim_editor &&
                                      !EDITOR_REFUSES.includes(
                                        c.claim_status,
                                      ) && (
                                        <button
                                          className="btn btn-secondary btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditorClaimId(c.id);
                                          }}
                                        >
                                          Edit all fields
                                        </button>
                                      )}
                                    <SubmitToQueueButton
                                      practiceId={selectedPractice?.id}
                                      claim={c}
                                      onPageChange={onPageChange}
                                    />
                                    <CopyLastChargeButton
                                      practiceId={selectedPractice?.id}
                                      claim={c}
                                      readOnly={readOnly}
                                      onCopied={() => loadClaims(claimsPage)}
                                    />
                                    {c.claim_status === "rejected" && (
                                      <button
                                        className="btn btn-primary btn-sm"
                                        onClick={(e) => handleRetry(c.id, e)}
                                        disabled={retryingClaimId === c.id}
                                      >
                                        {retryingClaimId === c.id
                                          ? "Retrying..."
                                          : "Retry"}
                                      </button>
                                    )}
                                    {(selectedPractice?.clearinghouse ===
                                      "stedi" ||
                                      correctionsCovered) &&
                                      ADJUDICATED.includes(c.claim_status) && (
                                        <button
                                          className="btn btn-primary btn-sm"
                                          title="Draft a formal replacement (frequency 7) or void (8) of this adjudicated claim"
                                          onClick={(e) => openCorrect(c, e)}
                                        >
                                          Correct
                                        </button>
                                      )}
                                    {c.parent_claim_id && (
                                      <span
                                        className="claim-correction-tag"
                                        title={`Frequency ${c.claim_frequency_code}`}
                                      >
                                        {c.claim_frequency_code === "8"
                                          ? "void of"
                                          : "corrects"}{" "}
                                        #{c.parent_claim_id}
                                      </span>
                                    )}
                                    <button
                                      className="btn btn-secondary btn-sm danger-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(c.id);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Right column: what has HAPPENED to the claim. One panel, two
                                sources - the comments people left and the events the system
                                recorded - because they answer the same question and nobody
                                should have to hunt in two places for it. Fixed height with its
                                own scroll, so the composer stays put and a claim with forty
                                events cannot push it off the screen. */}
                            <div className="detail-right">
                              <div className="detail-panel">
                                <div className="claim-panel-tabs">
                                  {(SHOW_CLAIM_COMMENTS
                                    ? ["comments", "history"]
                                    : ["history"]
                                  )
                                    .concat(rulesActive ? ["rules"] : [])
                                    .map((tab) => (
                                      <button
                                        key={tab}
                                        type="button"
                                        className={`claim-panel-tab${panelTabFor(detailTab, c.id) === tab ? " active" : ""}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDetailTab((prev) => ({
                                            ...prev,
                                            [c.id]: tab,
                                          }));
                                        }}
                                      >
                                        {tab === "comments"
                                          ? "Comments"
                                          : tab === "history"
                                            ? "History"
                                            : "Rules"}
                                      </button>
                                    ))}
                                </div>

                                <div
                                  className="detail-stream"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {panelTabFor(detailTab, c.id) === "rules" ? (
                                    <RulesPanel
                                      claimId={c.id}
                                      verdict={c.rules_verdict}
                                      detail={c.rules_verdict_detail}
                                      adviseMode={
                                        selectedPractice?.rules_mode ===
                                        "advise"
                                      }
                                    />
                                  ) : panelTabFor(detailTab, c.id) ===
                                    "comments" ? (
                                    commentLoadError[c.id] ? (
                                      // Never the empty state here. "No comments yet" is a statement
                                      // of fact, and a failed fetch has no idea whether it is true. A
                                      // biller acting on it would be acting on a claim they were told
                                      // was clear.
                                      <div className="stream-error">
                                        {commentLoadError[c.id]}{" "}
                                        <button
                                          className="btn btn-secondary btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            loadClaimComments(c.id);
                                          }}
                                        >
                                          Try again
                                        </button>
                                      </div>
                                    ) : !claimComments[c.id] ? (
                                      <div className="stream-muted">
                                        Loading comments...
                                      </div>
                                    ) : claimComments[c.id].length === 0 ? (
                                      <div className="stream-muted">
                                        No comments on this claim yet.
                                      </div>
                                    ) : (
                                      [...claimComments[c.id]]
                                        .reverse()
                                        .map((cm) => (
                                          <div
                                            key={cm.id}
                                            className="stream-entry"
                                          >
                                            <div className="stream-when">
                                              {cm.created_at
                                                ? new Date(
                                                    cm.created_at,
                                                  ).toLocaleString()
                                                : ""}
                                              {cm.author_name
                                                ? ` \u00b7 ${cm.author_name}`
                                                : ""}
                                            </div>
                                            <div className="stream-text">
                                              {cm.body}
                                            </div>
                                          </div>
                                        ))
                                    )
                                  ) : loadingTimeline &&
                                    !claimTimeline[c.id] ? (
                                    <div className="stream-muted">
                                      Loading history...
                                    </div>
                                  ) : (claimTimeline[c.id] || []).length ===
                                    0 ? (
                                    <div className="stream-muted">
                                      No history on this claim yet.
                                    </div>
                                  ) : (
                                    // Newest first. The route already returns them that way, so this
                                    // reads straight through. Comments run the other way on purpose:
                                    // their composer is at the foot of the column.
                                    (claimTimeline[c.id] || []).map((ev, i) => (
                                      <div key={i} className="stream-entry">
                                        <div className="stream-when">
                                          {ev.at
                                            ? new Date(ev.at).toLocaleString()
                                            : ""}
                                        </div>
                                        <div className="stream-text">
                                          {ev.label}
                                          {ev.source
                                            ? ` \u00b7 ${SOURCE_LABELS[ev.source] || ev.source}`
                                            : ""}
                                        </div>
                                        {typeof ev.detail === "string" &&
                                          ev.detail && (
                                            <div className="stream-sub">
                                              {ev.detail}
                                            </div>
                                          )}
                                        {ev.money && (
                                          <div className="stream-sub">
                                            {[
                                              `Billed ${formatCurrency(ev.money.billed)}`,
                                              `Paid ${formatCurrency((ev.money.insurance_paid || 0) + (ev.money.patient_payment || 0))}`,
                                              `Adj ${formatCurrency(ev.money.adjustment)}`,
                                              `Balance ${formatCurrency(ev.money.balance)}`,
                                            ].join(" \u00b7 ")}
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>

                                {panelTabFor(detailTab, c.id) === "comments" &&
                                  !readOnly && (
                                    <div
                                      className="claim-comment-add"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <textarea
                                        value={commentDrafts[c.id] || ""}
                                        maxLength={5000}
                                        rows={2}
                                        placeholder="Leave a comment on this claim"
                                        onChange={(e) =>
                                          setCommentDrafts((prev) => ({
                                            ...prev,
                                            [c.id]: e.target.value,
                                          }))
                                        }
                                      />
                                      <button
                                        className="btn btn-primary btn-sm"
                                        disabled={
                                          postingCommentFor === c.id ||
                                          !(commentDrafts[c.id] || "").trim()
                                        }
                                        onClick={() => handleAddComment(c.id)}
                                      >
                                        {postingCommentFor === c.id
                                          ? "Adding..."
                                          : "Add comment"}
                                      </button>
                                    </div>
                                  )}
                                {commentError[c.id] && (
                                  <div className="stream-error">
                                    {commentError[c.id]}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {claimsPages > 1 && (
            <div className="ledger-pagination">
              <button
                className="btn btn-secondary btn-sm"
                disabled={claimsPage <= 1}
                onClick={() => loadClaims(claimsPage - 1)}
              >
                Prev
              </button>
              <span className="pagination-info">
                Page {claimsPage} of {claimsPages} ({formatNumber(claimsTotal)}{" "}
                claims)
              </span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={claimsPage >= claimsPages}
                onClick={() => loadClaims(claimsPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Claim Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div
            className="modal-content claim-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Edit Claim</h2>
            {form.patient_name && (
              <div className="claim-modal-patient">
                {formatPatientName(form.patient_name)}
              </div>
            )}
            {formError && <div className="form-message error">{formError}</div>}
            <form onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-field full">
                  <label>Patient Name</label>
                  <input
                    type="text"
                    value={form.patient_name}
                    onChange={(e) =>
                      setForm({ ...form, patient_name: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Date of Service *</label>
                  <input
                    type="date"
                    min={DATE_INPUT_MIN}
                    max={DATE_INPUT_MAX}
                    value={form.date_of_service}
                    onChange={(e) =>
                      setForm({ ...form, date_of_service: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Provider</label>
                  <select
                    value={form.rendering_provider_id}
                    onChange={(e) => {
                      const prov = providers.find(
                        (p) => p.id === parseInt(e.target.value),
                      );
                      setForm({
                        ...form,
                        rendering_provider_id: e.target.value,
                        rendering_provider_name: prov ? prov.name : "",
                      });
                    }}
                  >
                    <option value="">-- Select --</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {(() => {
                const options = getInsuranceOptions();
                if (!editingClaim?.patient_id) {
                  return (
                    <div className="form-row">
                      <div className="form-field full">
                        <label>Insurance</label>
                        <div className="insurance-detail warn">
                          No patient linked — cannot select insurance.
                        </div>
                      </div>
                    </div>
                  );
                }
                if (!editingPatient) {
                  return (
                    <div className="form-row">
                      <div className="form-field full">
                        <label>Insurance</label>
                        <div className="insurance-detail">
                          Loading patient insurances...
                        </div>
                      </div>
                    </div>
                  );
                }
                if (options.length === 0) {
                  return (
                    <div className="form-row">
                      <div className="form-field full">
                        <label>Insurance</label>
                        <div className="insurance-detail warn">
                          No insurances on patient profile. Add one in Patients
                          to enable.
                        </div>
                      </div>
                    </div>
                  );
                }
                const selectedTier = getSelectedTier();
                return (
                  <div className="form-row">
                    <div className="form-field">
                      <label>Insurance</label>
                      <select
                        value={selectedTier}
                        onChange={(e) => handleInsuranceSelect(e.target.value)}
                      >
                        {!selectedTier && (
                          <option value="">-- Select insurance --</option>
                        )}
                        {options.map((o) => (
                          <option key={o.tier} value={o.tier}>
                            {o.payer_name || "(no payer name)"} ({o.label})
                          </option>
                        ))}
                      </select>
                      <div className="insurance-detail">
                        {form.payer_id
                          ? `Payer ID: ${form.payer_id}`
                          : "No payer ID"}
                        {!selectedTier && form.payer_id && (
                          <span className="insurance-drift">
                            {" "}
                            — not on patient profile
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Member ID</label>
                      <div className="member-id-display col-mono">
                        {form.member_id || (
                          <span className="insurance-detail">—</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ICD Codes */}
              <div className="form-section-label">ICD-10 Codes</div>
              {form.icd_codes.map((icd, idx) => (
                <div key={idx} className="icd-row">
                  <input
                    placeholder="ICD-10 code"
                    value={icd}
                    className="icd-input"
                    onChange={(e) => {
                      const u = [...form.icd_codes];
                      u[idx] = e.target.value;
                      setForm({ ...form, icd_codes: u });
                    }}
                  />
                  {form.icd_codes.length > 1 && (
                    <button
                      type="button"
                      className="btn-link danger"
                      onClick={() =>
                        setForm({
                          ...form,
                          icd_codes: form.icd_codes.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn-link"
                onClick={() =>
                  setForm({ ...form, icd_codes: [...form.icd_codes, ""] })
                }
              >
                + Add ICD
              </button>

              {/* CPT Codes */}
              <div className="form-section-label" style={{ marginTop: 12 }}>
                CPT Codes
              </div>
              {form.cpt_codes.map((cpt, idx) => (
                <div key={idx} className="cpt-row">
                  <input
                    placeholder="CPT"
                    value={cpt.code}
                    className="cpt-code-input"
                    onChange={(e) => {
                      const u = [...form.cpt_codes];
                      u[idx] = { ...cpt, code: e.target.value };
                      setForm({ ...form, cpt_codes: u });
                    }}
                  />
                  <input
                    placeholder="Modifiers"
                    value={cpt.modifiers}
                    className="cpt-mod-input"
                    onChange={(e) => {
                      const u = [...form.cpt_codes];
                      u[idx] = { ...cpt, modifiers: e.target.value };
                      setForm({ ...form, cpt_codes: u });
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Units"
                    value={cpt.units}
                    className="cpt-units-input"
                    min="1"
                    onChange={(e) => {
                      const u = [...form.cpt_codes];
                      u[idx] = { ...cpt, units: e.target.value };
                      setForm({ ...form, cpt_codes: u });
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Charge"
                    value={cpt.charge}
                    className="cpt-charge-input"
                    step="0.01"
                    onChange={(e) => {
                      const u = [...form.cpt_codes];
                      u[idx] = { ...cpt, charge: e.target.value };
                      setForm({ ...form, cpt_codes: u });
                    }}
                  />
                  {form.cpt_codes.length > 1 && (
                    <button
                      type="button"
                      className="btn-link danger"
                      onClick={() =>
                        setForm({
                          ...form,
                          cpt_codes: form.cpt_codes.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn-link"
                onClick={() =>
                  setForm({
                    ...form,
                    cpt_codes: [
                      ...form.cpt_codes,
                      { code: "", modifiers: "", units: 1, charge: "" },
                    ],
                  })
                }
              >
                + Add CPT
              </button>

              <div className="form-row" style={{ marginTop: 12 }}>
                <div className="form-field">
                  <label>Status</label>
                  <select
                    value={form.claim_status}
                    onChange={(e) =>
                      setForm({ ...form, claim_status: e.target.value })
                    }
                  >
                    {CLAIM_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Correct claim modal (B2) */}
      {correcting && (
        <div
          className="modal-overlay"
          onClick={() => !correcting.busy && setCorrecting(null)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Correct claim #{correcting.claim.id}</h2>
            <div className="claim-modal-patient">
              {formatPatientName(correcting.claim.patient_name)} · DOS{" "}
              {correcting.claim.date_of_service} · {correcting.claim.payer_name}{" "}
              · status {correcting.claim.claim_status}
            </div>
            {correcting.error && (
              <div className="form-message error">{correcting.error}</div>
            )}
            {correcting.info && !correcting.info.correctable && (
              <div className="form-message error">
                This claim cannot be corrected: it has not been adjudicated, or
                was never submitted from Atlas.
              </div>
            )}
            {correcting.info?.corrections?.length > 0 && (
              <div className="form-message">
                Already corrected by claim
                {correcting.info.corrections.length > 1 ? "s" : ""} #
                {correcting.info.corrections.join(", #")}.
              </div>
            )}
            <form onSubmit={submitCorrect}>
              <div className="form-row">
                <div className="form-field full">
                  <label>What are you sending?</label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="corr-freq"
                      value="7"
                      checked={correcting.frequency === "7"}
                      onChange={() =>
                        setCorrecting((c) => ({ ...c, frequency: "7" }))
                      }
                    />{" "}
                    Replacement (frequency 7) — a new draft copies this claim;
                    fix the lines, then submit it
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="corr-freq"
                      value="8"
                      checked={correcting.frequency === "8"}
                      onChange={() =>
                        setCorrecting((c) => ({ ...c, frequency: "8" }))
                      }
                    />{" "}
                    Void (frequency 8) — tells the payer to cancel this claim
                  </label>
                </div>
              </div>
              {correcting.info?.medicare ? (
                <div className="form-message">
                  Medicare: sent as an original (frequency 1) without a payer
                  claim number, per Medicare's rules. The link to this claim is
                  kept in Atlas.
                </div>
              ) : (
                <div className="form-row">
                  <div className="form-field full">
                    <label>
                      Payer claim control number (from the 835 / EOB)
                    </label>
                    <input
                      type="text"
                      value={correcting.pccn}
                      required
                      placeholder={
                        correcting.info?.payer_claim_control_number
                          ? ""
                          : "Not on file — enter it from the EOB"
                      }
                      onChange={(e) =>
                        setCorrecting((c) => ({ ...c, pccn: e.target.value }))
                      }
                    />
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-field full">
                  <label>Reason (kept on both claims)</label>
                  <input
                    type="text"
                    value={correcting.reason}
                    onChange={(e) =>
                      setCorrecting((c) => ({ ...c, reason: e.target.value }))
                    }
                    placeholder="e.g. units under-billed; wrong modifier"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCorrecting(null)}
                  disabled={correcting.busy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    correcting.busy ||
                    (correcting.info && !correcting.info.correctable)
                  }
                >
                  {correcting.busy ? "Working..." : "Create draft"}
                </button>
              </div>
              <p className="form-hint">
                Nothing is sent yet. The draft goes out through the normal
                Submit flow, with the same rule checks and cost confirmation.
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Upload CSV Modal */}
      {showUploadModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowUploadModal(false)}
        >
          <div
            className="modal-content upload-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Upload Claims</h2>

            {uploadError && (
              <div className="form-message error">{uploadError}</div>
            )}

            {!uploadPreview && !staleConfirm && (
              <>
                <div className="form-row">
                  <div className="form-field">
                    <label>File Format</label>
                    {/* Plain vendor uploads go through "Auto" (the validated async import). The old
                        per-vendor preview parsers were removed from the UI — they bypassed the async
                        pipeline and exposed a destructive "Replace Ledger" flow. Only the distinct
                        legacy flows (Encounter match-merge, admin Olympus) remain selectable. */}
                    <select
                      value={uploadFormat}
                      onChange={(e) => setUploadFormat(e.target.value)}
                    >
                      <option value="">Auto (recommended)</option>
                      <option value="practicesuite_encounter">
                        PracticeSuite Encounter Report (CSV)
                      </option>
                      {isAdmin && (
                        <option value="olympus_submission">
                          Olympus Submission
                        </option>
                      )}
                    </select>
                  </div>
                </div>
                {uploadFormat === "" && inappImportEnabled && (
                  <p className="upload-hint">
                    The claim source is detected from this practice's configured
                    system. The import runs in the background, so you can close
                    this and keep working.
                  </p>
                )}
                <div className="upload-drop-zone">
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={
                      uploadFormat === "" && inappImportEnabled
                        ? handleAsyncFileSelect
                        : handleUploadFileChange
                    }
                    disabled={uploading}
                    id="csv-upload-input"
                  />
                  <label
                    htmlFor="csv-upload-input"
                    className="upload-drop-label"
                  >
                    {uploading
                      ? "Uploading…"
                      : uploadFormat === "" && inappImportEnabled && asyncFile
                        ? asyncFile.name
                        : "Choose a CSV or XLS file or drag it here"}
                  </label>
                </div>
                {uploadFormat === "" && inappImportEnabled && (
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={
                        !asyncFile ||
                        uploading ||
                        importStatus?.status === "running"
                      }
                      onClick={() => submitImport(asyncFile)}
                    >
                      {uploading ? "Starting…" : "Start Import"}
                    </button>
                  </div>
                )}
              </>
            )}

            {!uploadPreview && staleConfirm && inappImportEnabled && (
              <>
                <div className="upload-stale-warning">
                  <p style={{ margin: 0 }}>{staleConfirm.error}</p>
                  <p style={{ margin: "10px 0 0" }}>
                    Newest date in this file:{" "}
                    <strong>{staleConfirm.fileThrough}</strong>
                    <br />
                    You already have data through:{" "}
                    <strong>{staleConfirm.importedThrough}</strong>
                  </p>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setStaleConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={uploading}
                    onClick={() => submitImport(staleConfirm.file, true)}
                  >
                    {uploading ? "Uploading…" : "Import anyway"}
                  </button>
                </div>
              </>
            )}

            {uploadPreview?._encounter && (
              <>
                <div className="upload-summary">
                  <span>
                    <strong>
                      {formatNumber(uploadPreview.summary?.rows_total || 0)}
                    </strong>{" "}
                    CSV rows parsed
                  </span>
                  <span>
                    <strong>
                      {formatNumber(uploadPreview.updates_planned)}
                    </strong>{" "}
                    claims to update
                  </span>
                  <span>
                    <strong>
                      {formatNumber(uploadPreview.creates_planned)}
                    </strong>{" "}
                    new claims to create
                  </span>
                </div>

                <div
                  className="encounter-summary-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                    margin: "16px 0",
                  }}
                >
                  <div>
                    <h4 style={{ margin: "0 0 6px", fontSize: 13 }}>
                      Patient matching
                    </h4>
                    <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                      Matched (name + DOB):{" "}
                      <strong>
                        {formatNumber(
                          uploadPreview.summary?.patient_match_name_dob || 0,
                        )}
                      </strong>
                      <br />
                      Matched (name fallback):{" "}
                      <strong>
                        {formatNumber(
                          uploadPreview.summary
                            ?.patient_match_name_only_fallback || 0,
                        )}
                      </strong>
                      <br />
                      Not matched:{" "}
                      <strong>
                        {formatNumber(
                          uploadPreview.summary?.patient_no_match || 0,
                        )}
                      </strong>
                    </div>
                  </div>
                  <div>
                    <h4 style={{ margin: "0 0 6px", fontSize: 13 }}>
                      Claim matching
                    </h4>
                    <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                      Clean match:{" "}
                      <strong>
                        {formatNumber(
                          uploadPreview.summary?.claim_clean_match || 0,
                        )}
                      </strong>
                      <br />
                      No match (will create):{" "}
                      <strong>
                        {formatNumber(
                          uploadPreview.summary?.claim_no_match || 0,
                        )}
                      </strong>
                      <br />
                      Ambiguous (skipped):{" "}
                      <strong>
                        {formatNumber(
                          uploadPreview.summary?.claim_ambiguous || 0,
                        )}
                      </strong>
                    </div>
                  </div>
                </div>

                {uploadPreview.summary?.no_patient_examples?.length > 0 && (
                  <div className="upload-warning-list">
                    <h4>Unmatched patients (sample)</h4>
                    {uploadPreview.summary.no_patient_examples.map((ex, i) => (
                      <div
                        key={i}
                        className="upload-error-item"
                        style={{ fontSize: 12 }}
                      >
                        {ex}
                      </div>
                    ))}
                  </div>
                )}

                {uploadPreview.summary?.create_examples?.length > 0 && (
                  <div className="upload-warning-list">
                    <h4>Sample new claims to create</h4>
                    {uploadPreview.summary.create_examples.map((ex, i) => (
                      <div
                        key={i}
                        className="upload-error-item"
                        style={{ fontSize: 12 }}
                      >
                        {ex}
                      </div>
                    ))}
                  </div>
                )}

                {uploadError && (
                  <div className="form-message error">{uploadError}</div>
                )}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowUploadModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleEncounterConfirm}
                    disabled={confirming}
                  >
                    {confirming
                      ? "Importing…"
                      : `Import ${formatNumber(uploadPreview.updates_planned + uploadPreview.creates_planned)} claims`}
                  </button>
                </div>
              </>
            )}

            {uploadPreview &&
              !uploadPreview._encounter &&
              uploadFormat === "olympus_submission" &&
              (() => {
                const includedClaims =
                  uploadPreview.claims?.filter(
                    (_, i) => !excludedRows.has(i),
                  ) || [];
                const hasInvalidIncluded = includedClaims.some(
                  (c) => !c.patient_found || !c.provider_found,
                );
                const canConfirm =
                  includedClaims.length > 0 && !hasInvalidIncluded;
                const excludedCount = excludedRows.size;

                const toggleExclude = (idx) => {
                  setExcludedRows((prev) => {
                    const next = new Set(prev);
                    if (next.has(idx)) next.delete(idx);
                    else next.add(idx);
                    return next;
                  });
                };

                return (
                  <>
                    <div className="upload-summary">
                      <span>
                        <strong>
                          {formatNumber(uploadPreview.summary?.valid || 0)}
                        </strong>{" "}
                        claims parsed
                      </span>
                      {excludedCount > 0 && (
                        <span className="upload-summary-warnings">
                          <strong>{excludedCount}</strong> excluded
                        </span>
                      )}
                      {uploadPreview.summary?.errors > 0 && (
                        <span className="upload-summary-errors">
                          <strong>
                            {formatNumber(uploadPreview.summary.errors)}
                          </strong>{" "}
                          errors
                        </span>
                      )}
                    </div>

                    {uploadPreview.errors?.length > 0 && (
                      <div className="upload-error-list">
                        <h4>Errors</h4>
                        {uploadPreview.errors.map((e, i) => (
                          <div key={i} className="upload-error-item">
                            {e}
                          </div>
                        ))}
                      </div>
                    )}

                    {uploadPreview.missing_patients?.length > 0 && (
                      <div className="warning-box warning-error">
                        <strong>Missing Patients</strong> — Add to Patients tab
                        or exclude rows
                        <div className="warning-tags">
                          {uploadPreview.missing_patients.map((name, i) => (
                            <span key={i} className="warning-tag error">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {uploadPreview.missing_providers?.length > 0 && (
                      <div className="warning-box warning-orange">
                        <strong>Missing Providers</strong> — Add to Practice
                        Details or exclude rows
                        <div className="warning-tags">
                          {uploadPreview.missing_providers.map((name, i) => (
                            <span key={i} className="warning-tag orange">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {uploadPreview.warnings?.length > 0 && (
                      <div className="upload-warning-list">
                        <h4>Warnings</h4>
                        {uploadPreview.warnings.map((w, i) => (
                          <div key={i} className="upload-warning-item">
                            {w}
                          </div>
                        ))}
                      </div>
                    )}

                    {uploadPreview.claims?.length > 0 && (
                      <div className="upload-preview-table-wrapper">
                        <table className="upload-preview-table">
                          <thead>
                            <tr>
                              <th style={{ width: 36 }}></th>
                              <th>Row</th>
                              <th>Date</th>
                              <th>Patient</th>
                              <th>Provider</th>
                              <th>Payer</th>
                              <th>CPT Codes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadPreview.claims.map((c, i) => {
                              const isExcluded = excludedRows.has(i);
                              const hasError =
                                !c.patient_found || !c.provider_found;
                              const rowClass = isExcluded
                                ? "row-excluded"
                                : hasError
                                  ? "row-error"
                                  : c.is_duplicate
                                    ? "upload-row-duplicate"
                                    : "";
                              return (
                                <tr key={i} className={rowClass}>
                                  <td>
                                    <button
                                      className={`btn-exclude ${isExcluded ? "excluded" : ""}`}
                                      onClick={() => toggleExclude(i)}
                                      title={
                                        isExcluded
                                          ? "Include row"
                                          : "Exclude row"
                                      }
                                    >
                                      {isExcluded ? "+" : "\u00d7"}
                                    </button>
                                  </td>
                                  <td>{c.row_num}</td>
                                  <td>{c.date_of_service}</td>
                                  <td>
                                    <span
                                      className={
                                        isExcluded ? "text-strike" : ""
                                      }
                                    >
                                      {formatPatientName(c.patient_name)}
                                    </span>
                                    {!c.patient_found && (
                                      <div className="text-error">
                                        NOT FOUND
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <span
                                      className={
                                        isExcluded ? "text-strike" : ""
                                      }
                                    >
                                      {c.rendering_provider_name || "-"}
                                    </span>
                                    {!c.provider_found && (
                                      <div className="text-warning">
                                        NOT FOUND
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {c.payer_name ||
                                      (c.patient_found ? "-" : "")}
                                  </td>
                                  <td className="col-mono">
                                    {(c.cpt_codes || [])
                                      .map((cp) => {
                                        const parts = [
                                          cp.code,
                                          ...(cp.modifiers || []),
                                        ];
                                        return cp.units > 1
                                          ? `${parts.join("-")}x${cp.units}`
                                          : parts.join("-");
                                      })
                                      .join(", ")}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {hasInvalidIncluded && (
                      <div
                        className="form-message error"
                        style={{ marginTop: 8 }}
                      >
                        Fix or exclude rows with missing data before confirming.
                      </div>
                    )}

                    <div className="modal-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setUploadPreview(null);
                          setUploadFile(null);
                          setUploadError(null);
                          setExcludedRows(new Set());
                        }}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowUploadModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUploadConfirm}
                        disabled={confirming || !canConfirm}
                      >
                        {confirming
                          ? "Creating..."
                          : `Confirm & Create ${formatNumber(includedClaims.length)} Claims`}
                      </button>
                    </div>
                  </>
                );
              })()}

            {uploadPreview &&
              !uploadPreview._encounter &&
              uploadFormat !== "olympus_submission" && (
                <>
                  <div className="upload-summary">
                    <span>
                      <strong>
                        {formatNumber(uploadPreview.summary?.valid || 0)}
                      </strong>{" "}
                      valid claims
                    </span>
                    {uploadPreview.summary?.errors > 0 && (
                      <span className="upload-summary-errors">
                        <strong>
                          {formatNumber(uploadPreview.summary.errors)}
                        </strong>{" "}
                        errors
                      </span>
                    )}
                    {uploadPreview.summary?.warnings > 0 && (
                      <span className="upload-summary-warnings">
                        <strong>
                          {formatNumber(uploadPreview.summary.warnings)}
                        </strong>{" "}
                        warnings
                      </span>
                    )}
                  </div>

                  {uploadPreview.errors?.length > 0 && (
                    <div className="upload-error-list">
                      <h4>Errors</h4>
                      {uploadPreview.errors.map((e, i) => (
                        <div key={i} className="upload-error-item">
                          {e}
                        </div>
                      ))}
                    </div>
                  )}

                  {uploadPreview.warnings?.length > 0 && (
                    <div className="upload-warning-list">
                      <h4>Warnings</h4>
                      {uploadPreview.warnings.map((w, i) => (
                        <div key={i} className="upload-warning-item">
                          {w}
                        </div>
                      ))}
                    </div>
                  )}

                  {uploadPreview.claims?.length > 0 &&
                    (() => {
                      const hasFinancials = uploadPreview.claims.some(
                        (c) => c.claim_status !== undefined,
                      );
                      const hasCpts = uploadPreview.claims.some(
                        (c) => c.cpt_codes?.length > 0,
                      );
                      const previewRows = uploadPreview.claims.slice(0, 50);
                      return (
                        <>
                          <div className="upload-preview-table-wrapper">
                            <table className="upload-preview-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Patient</th>
                                  <th>Provider</th>
                                  <th>Payer</th>
                                  {hasCpts && <th>CPTs</th>}
                                  <th>Billed</th>
                                  {hasFinancials && <th>Paid</th>}
                                  {hasFinancials && <th>Adj</th>}
                                  {hasFinancials && <th>Balance</th>}
                                  {hasFinancials && <th>Status</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {previewRows.map((c, i) => (
                                  <tr
                                    key={i}
                                    className={
                                      c.is_duplicate
                                        ? "upload-row-duplicate"
                                        : ""
                                    }
                                  >
                                    <td>{c.date_of_service}</td>
                                    <td>{formatPatientName(c.patient_name)}</td>
                                    <td>{c.rendering_provider_name || "-"}</td>
                                    <td>{c.payer_name || "-"}</td>
                                    {hasCpts && (
                                      <td className="col-mono">
                                        {(c.cpt_codes || [])
                                          .map((cp) =>
                                            cp.units > 1
                                              ? `${cp.code}x${cp.units}`
                                              : cp.code,
                                          )
                                          .join(", ")}
                                      </td>
                                    )}
                                    <td className="col-currency">
                                      {formatCurrency(c.billed_amount)}
                                    </td>
                                    {hasFinancials && (
                                      <td className="col-currency">
                                        {formatCurrency(
                                          (c.insurance_paid || 0) +
                                            (c.patient_payment || 0),
                                        )}
                                      </td>
                                    )}
                                    {hasFinancials && (
                                      <td className="col-currency">
                                        {formatCurrency(c.adjustment_amount)}
                                      </td>
                                    )}
                                    {hasFinancials && (
                                      <td className="col-currency">
                                        {formatCurrency(c.balance)}
                                      </td>
                                    )}
                                    {hasFinancials && (
                                      <td>
                                        <span
                                          className="ledger-status-badge"
                                          style={{
                                            background: statusColor(
                                              displayStatusKey(c.claim_status, readOnly),
                                            ),
                                          }}
                                        >
                                          {displayStatusKey(c.claim_status, readOnly)}
                                        </span>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {uploadPreview.claims.length > 50 && (
                            <div className="upload-preview-truncated">
                              Showing 50 of{" "}
                              {formatNumber(uploadPreview.claims.length)} claims
                            </div>
                          )}
                        </>
                      );
                    })()}

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setUploadPreview(null);
                        setUploadFile(null);
                        setUploadError(null);
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowUploadModal(false)}
                    >
                      Cancel
                    </button>
                    {uploadPreview.summary?.can_proceed && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUploadConfirm}
                        disabled={confirming}
                      >
                        {confirming
                          ? "Creating..."
                          : `Confirm & Create ${formatNumber(uploadPreview.claims.length)} Claims`}
                      </button>
                    )}
                  </div>
                </>
              )}

            {!uploadPreview && (
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Post-upload notice — surfaces only when the import created stub
          payer rows that need IDs filled in on the Practice Details page. */}
      {pendingPayerNotice && (
        <div
          className="modal-overlay"
          onClick={() => setPendingPayerNotice(null)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 440 }}
          >
            <h2>
              {pendingPayerNotice.count} payer
              {pendingPayerNotice.count === 1 ? "" : "s"} need payer IDs
            </h2>
            <p style={{ color: "var(--text-secondary)", marginBottom: 20 }}>
              Add them on the Practice Details page so eligibility checks can
              run for these claims.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPendingPayerNotice(null)}
              >
                Dismiss
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setPendingPayerNotice(null);
                  if (onPageChange) onPageChange("practice-details");
                }}
              >
                Go to Practice Details
              </button>
            </div>
          </div>
        </div>
      )}

      {resendPrompt && (
        <div className="modal-overlay" onClick={() => answerResend(false)}>
          <div
            className="modal-content"
            role="alertdialog"
            aria-labelledby="resend-prompt-title"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Escape") answerResend(false);
            }}
            style={{ maxWidth: 480 }}
          >
            <h2 id="resend-prompt-title">Send claims again?</h2>
            <p style={{ color: "var(--text-secondary)", marginBottom: 20 }}>
              {resendPromptText(resendPrompt.count, resendPrompt.total)}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => answerResend(true)}
              >
                Send anyway
              </button>
              {/* Focused, so Enter means No. */}
              <button
                type="button"
                className="btn btn-primary"
                autoFocus
                onClick={() => answerResend(false)}
              >
                No, skip them
              </button>
            </div>
          </div>
        </div>
      )}

      {statusModal && (
        <div className="modal-overlay" onClick={() => setStatusModal(null)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 440 }}
          >
            <h2>{statusModal.title}</h2>
            <p style={{ color: "var(--text-secondary)", marginBottom: 20 }}>
              {statusModal.message}
            </p>
            <div className="modal-actions">
              {statusModal.onConfirm ? (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setStatusModal(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const fn = statusModal.onConfirm;
                      setStatusModal(null);
                      fn();
                    }}
                  >
                    {statusModal.confirmLabel || "Confirm"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setStatusModal(null)}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {editorClaimId && (
        <div
          className="claim-editor-drawer-backdrop"
          onClick={closeEditorDrawer}
        >
          <aside
            className="claim-editor-drawer"
            role="dialog"
            aria-label={`Claim ${editorClaimId}`}
            onClick={(e) => e.stopPropagation()}
          >
            <ClaimEditor
              selectedPractice={selectedPractice}
              claimId={editorClaimId}
              readOnly={readOnly}
              isAdmin={isAdmin}
              features={features}
              onDirtyChange={setEditorDirty}
              onClose={closeEditorDrawer}
              onNotFound={() => {
                setEditorDirty(false);
                setEditorClaimId(null);
              }}
            />
          </aside>
        </div>
      )}
    </div>
  );
}

export default ClaimsLedgerPage;
